import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useToast } from '../../components/Toast';
import {
  checkSingleSentence,
  checkBatchStream,
  getInspectorCheckJobStatus,
  getInspectorRecord,
  getFriendlyInspectorErrorMessage,
  refineInspectorContent,
  updateInspectorRecord,
  updateBatchInspectorResult
} from '../../api/inspectorApi';
import { preCheckFiles } from '../../api/chatApi';
import { getFileLimits } from '../../constants/fileLimits';
import {
  SUPPORTED_UPLOAD_ACCEPT,
  isClearlyBlockedUploadFile,
} from '../../constants/fileTypes';
import { getApiUrl } from '../../utils/apiConfig';
import { fetchWithRetry, calcApiTimeout } from '../../utils/fetchWithRetry';
import { isRateLimitError, showRateLimitAlert } from '../../utils/rateLimitHandler';
import { calculateNEISBytes as calculateNeisBytes } from '../../utils/neisBytes';
import { PRO_ACCESS, resolveProAccess } from '../../utils/proAccessGuard';
import {
  filterLocatedInspectorViolations,
  guidelinePageLabel,
  sanitizeInspectorResult,
} from '../../utils/inspectorViolations';
import { assertInspectorContextResponse } from '../../utils/inspectorContextContract';
import axios from 'axios';
import CountUp from 'react-countup';
import LoginPrompt from '../../Auth/LoginPrompt';
import ProUpgradeModal from '../../components/chat/ProUpgradeModal';
import FilePreCheckModal from '../../components/common/FilePreCheckModal';
import FieldHelpTooltip from '../../components/common/FieldHelpTooltip';
import Dialog from '../../components/common/Dialog';
import ModalActions from '../../components/common/ModalActions';
import Notice from '../../components/common/Notice';
import TossLoadingSpinner from '../../components/layout/TossLoadingSpinner';
import BatchLoadingMiniGame from '../../components/common/BatchLoadingMiniGame';
import InspectorClassCheck, { InspectorOverlapSentences } from './InspectorClassCheck';
import { overlapExcelCell, planInspectorOverlap } from '../../utils/inspectorClassOverlap';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';
import * as XLSX from 'xlsx';
// 📊 GA4 이벤트 트래킹
import { trackInspectorUse } from '../../utils/analytics';
import { trackEvent, trackRiseFeatureAction } from '../../utils/eventTracker';
import InspectorGuidelineContextSelector, {
  CURRENT_INSPECTOR_ACADEMIC_YEAR,
  getInspectorGuidelineContextLabel,
  isValidInspectorGrade,
  normalizeInspectorAcademicYear,
} from './InspectorGuidelineContextSelector';
import './SaenggibuInspector.css';
import '../../components/chat/batch/Step1_ExcelUpload.css';
import '../../components/chat/batch/BatchStepShared.css';
// 검사 진행(batchStep 2) 화면이 .saenggibu-batch-processing / -progress-section 카드 스타일을 쓴다.
// 이 페이지로 바로 들어오면 Step4 청크가 아직 없어 카드 배경·여백이 빠진 채 렌더되던 것을 막는다.
import '../../components/chat/batch/Step4_Processing.css';

const INSPECTOR_BATCH_HELP = {
  upload: {
    title: '어떤 파일을 올리면 좋나요?',
    items: [
      '나이스에서 내려받은 파일, 엑셀, 한글, PDF, 이미지처럼 학생명과 생기부 문장이 함께 보이는 자료를 올려주세요.',
      '정해진 양식이 아니어도 AI가 학생명과 내용을 찾아 전체 검사를 준비해요.',
      '학생명과 생기부 내용이 모두 있어야 정확하게 나눠서 검사할 수 있어요.',
    ],
  },
  paste: {
    title: '엑셀 표는 어떻게 붙여넣나요?',
    items: [
      '엑셀에서 학생명과 생기부 내용 열을 복사해 아래 표 첫 칸에 그대로 붙여넣으세요.',
      '열 이름이 달라도 괜찮고, 20명보다 많으면 행이 자동으로 늘어나요.',
      '붙여넣은 내용은 바로 기재요령과 맞춤법 기준으로 검사해요.',
    ],
  },
};

// 🎨 카테고리별 색상 (Gemini API category 필드 기준)
const CATEGORY_COLORS = {
  ending: '#3b82f6',       // 파란색 - 종결어미 (가장 흔함)
  brand: '#dc2626',        // 빨간색 - 브랜드명 (심각)
  institution: '#ea580c',  // 주황색 - 기관/대회명 (중요)
  expression: '#ca8a04',   // 노란색 - 자소서식 표현 (주의)
  spelling: '#0891b2',     // 청록색 - 맞춤법 (경미)
  word: '#7c3aed',         // 보라색 - 금지 어휘 (주의)
  other: '#6b7280',        // 회색 - 기타
};

// 위반사항 심각도 정의 (숫자가 클수록 심각) - 레거시 호환용
const VIOLATION_SEVERITY = {
  // 심각 (절대 금지 - 레벨 4)
  '브랜드명/상표명': 4,
  '브랜드명 기재 금지': 4,
  '브랜드/상호명 기재 금지': 4,
  '교육기관명': 4,
  '특정 기관/대회명 기재 금지': 4,
  '외모/신체': 4,
  '가정환경': 4,
  '건강/질병 정보': 4,
  '종교 관련': 4,
  '비교/서열화': 4,
  '부정적 표현': 4,
  '금지 표현': 4,
  
  // 중요 (수정 필요 - 레벨 3)
  '자소서식 표현': 3,
  '잘못된 어미': 3,
  '종결어미': 3,
  
  // 주의 (개선 필요 - 레벨 2)
  '모호한 표현': 2,
  '구체성 부족': 2,
  '추측성 표현': 2,
  '객관성 부족': 2,
  '성장 가능성 누락': 2,
  
  // 경미 (다듬기 - 레벨 1)
  '중복/오류': 1,
  '맞춤법 오류': 1,
  '오탈자': 1
};

// 심각도별 색상 (Semantic 색상 시스템)
const SEVERITY_COLORS = {
  4: '#dc2626',  // 심각 - 빨강
  3: '#ea580c',  // 중요 - 주황
  2: '#ca8a04',  // 주의 - 노랑
  1: '#2563eb'   // 경미 - 파랑
};

const INSPECTOR_TEMPLATE_ROWS = [
  ['번호', '학생명', '생기부 내용'],
  ['1', '김왓퀴', '학급 회의에서 친구 의견을 먼저 듣고 정리해 발표함. 역할 분담이 필요한 상황에서 조용히 필요한 일을 맡아 마무리함.'],
  ['2', '이퀴즈', '과제 준비물을 자주 챙기며 모둠 활동에서 자료 조사와 정리를 담당함. 발표 전 친구가 어려워하는 부분을 설명해 줌.'],
  ['3', '박생기', '수업 중 질문을 통해 내용을 확인하고 자신의 생각을 구체적으로 말함. 갈등 상황에서 감정을 가라앉히고 대화로 해결하려는 모습을 보임.']
];

const INSPECTOR_PASTE_GRID_INITIAL_ROWS = 20;
const INSPECTOR_REFINE_PRESETS = [
  { id: 'fix_violations', mode: 'fix_violations', label: '위반만 고쳐줘', instruction: '검사에서 발견된 위반사항만 정확히 고치고 나머지 내용은 최대한 유지해줘.' },
  { id: 'natural', mode: 'natural', label: '자연스럽게', instruction: '위반사항을 해결하면서 교사가 바로 쓸 수 있게 자연스럽게 다듬어줘.' },
  { id: 'reduce_bytes', mode: 'reduce_bytes', label: '조금 줄여줘', instruction: '핵심 사실은 유지하고 바이트를 조금 줄여줘.' },
];

const createEmptyInspectorPasteRows = (count = INSPECTOR_PASTE_GRID_INITIAL_ROWS) => (
  Array.from({ length: count }, (_, index) => ({
    no: String(index + 1),
    name: '',
    content: ''
  }))
);

const INSPECTOR_NAME_HEADERS = ['이름', '학생명', '성명', '학생 이름', '학생성명', '명렬', '명단'];
const INSPECTOR_CONTENT_HEADERS = ['생기부내용', '생기부 내용', '생기부', '행발', '행동발달', '종합의견', '세특', '창체', '진로', '내용', '문장'];

const normalizeInspectorHeader = (value) => String(value || '').replace(/\s+/g, '').trim().toLowerCase();

const findInspectorHeaderIndex = (headers, candidates) => {
  const normalizedCandidates = candidates.map(normalizeInspectorHeader);
  return headers.findIndex(header => normalizedCandidates.some(candidate => normalizeInspectorHeader(header).includes(candidate)));
};

const normalizeInspectorPastedRows = (text) => {
  try {
    const workbook = XLSX.read(text, { type: 'string' });
    const firstSheetName = workbook.SheetNames[0];
    if (firstSheetName) {
      const rows = XLSX.utils
        .sheet_to_json(workbook.Sheets[firstSheetName], { header: 1, raw: false, defval: '' })
        .map(row => row.map(cell => String(cell || '').trim()))
        .filter(row => row.some(cell => cell));
      if (rows.length > 0) return rows;
    }
  } catch {
    // Fall through to lightweight TSV/CSV parsing.
  }

  return text
    .split(/\r?\n/)
    .map(line => {
      const delimiter = line.includes('\t') ? '\t' : ',';
      return line.split(delimiter).map(cell => String(cell || '').trim());
    })
    .filter(row => row.some(cell => cell));
};

const shouldHydrateInspectorGridFromClipboard = (text) => {
  const normalized = String(text || '').trim();
  if (!normalized) return false;

  const rows = normalizeInspectorPastedRows(normalized);
  if (rows.length <= 1) return false;

  const maxColumns = Math.max(...rows.map(row => row.length));
  const hasHeader = rows.slice(0, 5).some(row => (
    findInspectorHeaderIndex(row, INSPECTOR_NAME_HEADERS) >= 0 &&
    findInspectorHeaderIndex(row, INSPECTOR_CONTENT_HEADERS) >= 0
  ));

  return hasHeader || maxColumns >= 2;
};

const parseInspectionStudentsFromPastedTable = (text) => {
  const rows = normalizeInspectorPastedRows(text);
  if (rows.length === 0) return [];

  const headerRowIndex = rows.findIndex((row, index) => (
    index < 5 &&
    findInspectorHeaderIndex(row, INSPECTOR_NAME_HEADERS) >= 0 &&
    findInspectorHeaderIndex(row, INSPECTOR_CONTENT_HEADERS) >= 0
  ));

  const hasHeader = headerRowIndex >= 0;
  const headers = hasHeader ? rows[headerRowIndex] : [];
  const dataRows = rows.slice(hasHeader ? headerRowIndex + 1 : 0);
  let nameIndex = hasHeader ? findInspectorHeaderIndex(headers, INSPECTOR_NAME_HEADERS) : -1;
  let contentIndexes = hasHeader
    ? headers
        .map((header, index) => ({ header, index }))
        .filter(({ index }) => index !== nameIndex)
        .filter(({ header }) => INSPECTOR_CONTENT_HEADERS.some(candidate => normalizeInspectorHeader(header).includes(normalizeInspectorHeader(candidate))))
        .map(({ index }) => index)
    : [];

  if (!hasHeader) {
    const maxColumns = Math.max(...rows.map(row => row.length));
    if (maxColumns >= 3) {
      nameIndex = 1;
      contentIndexes = Array.from({ length: maxColumns - 2 }, (_, index) => index + 2);
    } else if (maxColumns >= 2) {
      nameIndex = 0;
      contentIndexes = [1];
    }
  }

  if (nameIndex < 0 || contentIndexes.length === 0) return [];

  const parsedStudents = dataRows
    .map((row, index) => {
      const name = String(row[nameIndex] || '').trim();
      const content = contentIndexes
        .map(contentIndex => String(row[contentIndex] || '').trim())
        .filter(Boolean)
        .join('\n');

      return {
        id: `pasted_inspector_${index + 1}`,
        name: name || `학생${index + 1}`,
        content,
      };
    })
    .filter(student => student.name && student.content);
  return parsedStudents;
};

const buildInspectorGridRowsFromText = (text) => {
  const students = parseInspectionStudentsFromPastedTable(text);
  if (students.length > 0) {
    const rows = students.map((student, index) => ({
      no: String(index + 1),
      name: student.name || '',
      content: student.content || ''
    }));
    return rows.length < INSPECTOR_PASTE_GRID_INITIAL_ROWS
      ? [...rows, ...createEmptyInspectorPasteRows(INSPECTOR_PASTE_GRID_INITIAL_ROWS - rows.length).map((row, idx) => ({ ...row, no: String(rows.length + idx + 1) }))]
      : rows;
  }

  return createEmptyInspectorPasteRows();
};

const inspectorGridRowsToText = (rows) => {
  const filledRows = rows.filter(row => row.name.trim() || row.content.trim());
  if (filledRows.length === 0) return '';

  return [
    ['번호', '학생명', '생기부 내용'].join('\t'),
    ...filledRows.map((row, index) => [
      row.no || String(index + 1),
      row.name,
      row.content
    ].join('\t'))
  ].join('\n');
};

const SaenggibuInspector = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, isAnonymous } = useAuth();
  const { subscriptionStatus, fetchSubscriptionStatus } = useSubscription();
  const { showToast } = useToast();
  
  // Window size for confetti
  const { width, height } = useWindowSize();
  
  // PRO 사용자 여부
  const isProUser = subscriptionStatus?.isPro || false;
  
  const isAnonymousUser = isAnonymous || !currentUser;
  
  // 비로그인 1회 체험 카운터
  const [anonCheckCount, setAnonCheckCount] = useState(() => {
    return parseInt(sessionStorage.getItem('inspector-anon-check-count') || '0');
  });
  const [loginPromptFeature, setLoginPromptFeature] = useState('inspector');
  
  // 모드 상태
  const [mode, setMode] = useState('single'); // 'single', 'multiple', 'batch'
  
  // 단일 검사 상태
  const [inputText, setInputText] = useState('');
  const [schoolLevel, setSchoolLevel] = useState('elementary');
  const [academicYear, setAcademicYear] = useState(CURRENT_INSPECTOR_ACADEMIC_YEAR);
  const [recordGrade, setRecordGrade] = useState('');
  const [showGuidelineContextError, setShowGuidelineContextError] = useState(false);
  const [selectedModel, setSelectedModel] = useState('gemini-2.5-flash-lite');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedViolation, setSelectedViolation] = useState(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showProModal, setShowProModal] = useState(false);
  const [refinePanelOpen, setRefinePanelOpen] = useState(false);
  const [refineMode, setRefineMode] = useState('fix_violations');
  const [refineInstruction, setRefineInstruction] = useState('');
  const [refineLoading, setRefineLoading] = useState(false);
  const [refinePreview, setRefinePreview] = useState(null);
  const [refinePreviewDraft, setRefinePreviewDraft] = useState('');
  const [manualEditOpen, setManualEditOpen] = useState(false);
  const [manualDraft, setManualDraft] = useState('');
  const [savingRefine, setSavingRefine] = useState(false);
  
  // 🎉 완벽 축하 상태
  const [showPerfectCelebration, setShowPerfectCelebration] = useState(false);
  const [confettiConfig, setConfettiConfig] = useState(null);
  
  // 엑셀 일괄 검사 상태
  const [batchFile, setBatchFile] = useState(null);
  // const [batchStudents, setBatchStudents] = useState([]); // 사용하지 않음 - 제거
  const [batchResults, setBatchResults] = useState(null);
  // 스캔 PDF·사진에서 읽은 반은 글자 인식 오차가 있어 겹침을 '거의 같은 문장'으로만 보여 준다.
  const [batchApproximate, setBatchApproximate] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchStep, setBatchStep] = useState(1); // 1:업로드, 2:검사중, 3:결과
  const [selectedBatchStudent, setSelectedBatchStudent] = useState(0); // Step3에서 선택된 학생 인덱스
  const [batchPasteText, setBatchPasteText] = useState('');
  const [batchPasteRows, setBatchPasteRows] = useState(() => createEmptyInspectorPasteRows());
  const [batchPasteError, setBatchPasteError] = useState(null);
  const [batchPasteLoading, setBatchPasteLoading] = useState(false);
  const [showInspectorTemplateGuide, setShowInspectorTemplateGuide] = useState(false);
  
  // 드래그 앤 드롭 상태
  const [isDragging, setIsDragging] = useState(false);
  
  // 에러 상태
  const [uploadError, setUploadError] = useState(null);
  const [pendingTruncatedInspection, setPendingTruncatedInspection] = useState(null);
  // 스캔 PDF(Cloud Vision 행 분할) 인식 결과 미리보기 — 채점의 범위 확인 스텝과 같은 역할.
  const [pendingScanPreview, setPendingScanPreview] = useState(null);
  
  // 🔍 파일 사전 검사 상태
  const [showPreCheckModal, setShowPreCheckModal] = useState(false);
  const [preCheckResult, setPreCheckResult] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [preCheckLoading, setPreCheckLoading] = useState(false);
  
  // Value First: 사용자 수 통계 (사회적 증거)
  const [teacherCount, setTeacherCount] = useState(1500);
  
  // 🔧 함수 참조를 위한 ref (useCallback 순서 독립성 확보)
  const actualExcelUploadRef = useRef();
  
  // Ref (자동 스크롤용)
  const detailPanelRef = useRef(null);
  const resultSectionRef = useRef(null);
  const restoredInspectorRecordRef = useRef(null);
  const currentResultKeyRef = useRef('');
  const proAccessCheckRef = useRef(false);
  
  // 🆕 스크롤 및 하이라이트를 위한 refs
  const modeButtonsRef = useRef(null);
  const inspectorAssistDetailsRef = useRef(null);

  const requireProAccess = useCallback(async (loginFeature = 'inspector-batch') => {
    if (!currentUser) {
      setLoginPromptFeature(loginFeature);
      setShowLoginPrompt(true);
      return false;
    }

    if (proAccessCheckRef.current) return false;
    proAccessCheckRef.current = true;

    const needsServerCheck = (
      subscriptionStatus === null
      || subscriptionStatus?.verificationFailed
    );
    if (needsServerCheck) {
      showToast('PRO 이용 여부를 확인하고 있어요', 'info', 2000);
    }

    try {
      const access = await resolveProAccess({
        subscriptionStatus,
        fetchSubscriptionStatus,
      });

      if (access === PRO_ACCESS.PRO) return true;
      if (access === PRO_ACCESS.FREE) {
        setShowProModal(true);
        return false;
      }

      showToast(
        'PRO 이용 여부를 확인하지 못했어요. 잠시 후 다시 시도해주세요.',
        'warning'
      );
      return false;
    } finally {
      proAccessCheckRef.current = false;
    }
  }, [currentUser, subscriptionStatus, fetchSubscriptionStatus, showToast]);
  
  useEffect(() => {
    if (currentUser) return;
    axios.get(getApiUrl('/api/public-stats'))
      .then(res => { if (res.data?.total_teachers) setTeacherCount(res.data.total_teachers); })
      .catch(() => {});
  }, [currentUser]);
  
  // PRO 유저일 때 고급 모드 기본 선택
  useEffect(() => {
    if (isProUser) {
      setSelectedModel('gemini-2.5-flash');
    }
  }, [isProUser]);
  
  // 로그인 후 비로그인 시 저장된 입력 데이터 복원
  useEffect(() => {
    if (currentUser && !isAnonymous) {
      const savedText = localStorage.getItem('inspector-saved-text');
      const savedSchool = localStorage.getItem('inspector-saved-school');
      const savedYear = localStorage.getItem('inspector-saved-academic-year');
      const savedGrade = localStorage.getItem('inspector-saved-record-grade');
      const restoredSchool = savedSchool || schoolLevel;
      if (savedText) {
        setInputText(savedText);
        localStorage.removeItem('inspector-saved-text');
      }
      if (savedSchool) {
        setSchoolLevel(savedSchool);
        localStorage.removeItem('inspector-saved-school');
      }
      if (savedYear) {
        setAcademicYear(normalizeInspectorAcademicYear(restoredSchool, savedYear));
        localStorage.removeItem('inspector-saved-academic-year');
      }
      if (savedGrade) {
        setRecordGrade(
          isValidInspectorGrade(restoredSchool, savedGrade)
            ? Number(savedGrade)
            : '',
        );
        localStorage.removeItem('inspector-saved-record-grade');
      }
    }
  }, [currentUser, isAnonymous, schoolLevel]);
  
  // 비로그인 입력 데이터 자동 저장 (디바운싱)
  useEffect(() => {
    if (isAnonymousUser && inputText.trim()) {
      const timer = setTimeout(() => {
        localStorage.setItem('inspector-saved-text', inputText);
        localStorage.setItem('inspector-saved-school', schoolLevel);
        localStorage.setItem('inspector-saved-academic-year', String(academicYear));
        localStorage.setItem('inspector-saved-record-grade', String(recordGrade || ''));
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [inputText, schoolLevel, academicYear, recordGrade, isAnonymousUser]);
  
  // 🎉 다양한 색종이 효과 설정
  const getRandomConfettiConfig = () => {
    const configs = [
      // 1. 클래식 색종이 - 중간 속도
      {
        numberOfPieces: 150,
        gravity: 0.25,
        colors: ['#fbbf24', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'],
        recycle: false,
        wind: 0
      },
      // 2. 폭죽 대폭발! - 엄청 많이, 빠르게
      {
        numberOfPieces: 500,
        gravity: 0.8,
        colors: ['#ef4444', '#f59e0b', '#eab308', '#22c55e', '#3b82f6', '#a855f7', '#ec4899', '#f472b6'],
        recycle: false,
        wind: 0.05,
        friction: 0.92
      },
      // 3. 천천히 황금빛 - 적은 양, 매우 느림
      {
        numberOfPieces: 100,
        gravity: 0.08,
        colors: ['#fbbf24', '#facc15', '#fde047', '#fef08a'],
        recycle: false,
        wind: 0
      },
      // 4. 무지개 폭포 - 많은 양
      {
        numberOfPieces: 350,
        gravity: 0.5,
        colors: ['#ef4444', '#f97316', '#fbbf24', '#84cc16', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6', '#d946ef', '#ec4899'],
        recycle: false,
        wind: 0.02
      },
      // 5. 좌우로 흩날리는 효과 - 바람 강함
      {
        numberOfPieces: 200,
        gravity: 0.3,
        colors: ['#f59e0b', '#eab308', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'],
        recycle: false,
        wind: 0.08,
        friction: 0.98
      }
    ];
    
    const selectedConfig = configs[Math.floor(Math.random() * configs.length)];
    
    return selectedConfig;
  };

  const resetRefineUi = useCallback(() => {
    setRefinePanelOpen(false);
    setRefineInstruction('');
    setRefinePreview(null);
    setRefinePreviewDraft('');
    setManualEditOpen(false);
    setManualDraft('');
  }, []);

  const replaceInspectorDraft = useCallback((nextText) => {
    setInputText(nextText);
    setResult(null);
    setSelectedViolation(null);
    setShowPerfectCelebration(false);
    setConfettiConfig(null);
    resetRefineUi();
  }, [resetRefineUi]);

  // 학교급 변경 시 결과 초기화
  const handleSchoolLevelChange = (level) => {
    setSchoolLevel(level);
    setAcademicYear((year) => normalizeInspectorAcademicYear(level, year));
    setRecordGrade((grade) => (
      isValidInspectorGrade(level, grade) ? grade : ''
    ));
    setShowGuidelineContextError(false);
    setResult(null);
    setSelectedViolation(null);
    resetRefineUi();
  };

  const handleAcademicYearChange = (year) => {
    setAcademicYear(normalizeInspectorAcademicYear(schoolLevel, year));
    setResult(null);
    setSelectedViolation(null);
    resetRefineUi();
  };

  const handleRecordGradeChange = (grade) => {
    setRecordGrade(grade);
    setShowGuidelineContextError(false);
    setResult(null);
    setSelectedViolation(null);
    resetRefineUi();
  };

  const validateGuidelineContext = useCallback(() => {
    if (isValidInspectorGrade(schoolLevel, recordGrade)) return true;
    setShowGuidelineContextError(true);
    showToast('기록을 작성한 당시 학년을 선택해주세요.', 'warning');
    return false;
  }, [recordGrade, schoolLevel, showToast]);
  
  // 엑셀 일괄 검사 핸들러들
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadRemainingTime, setUploadRemainingTime] = useState(60); // 전체 검사는 60초
  const [batchProgress, setBatchProgress] = useState(0);
  const [currentChecking, setCurrentChecking] = useState('');
  // 서버가 보내주는 실제 검사 완료 인원 (하트비트 스트림의 청크 진행률)
  const [batchCheckCounts, setBatchCheckCounts] = useState(null);
  const uploadProgressIntervalRef = useRef(null);
  // 서버 진행률 기반 최저선(%) — 시뮬레이션이 서버 진행률보다 뒤로 가지 않게 한다
  const batchServerFloorRef = useRef(0);
  const batchHasServerCountsRef = useRef(false);
  // 재접속/합류 안내 메시지가 시뮬레이션 메시지에 바로 덮이지 않도록 잠깐 고정
  const batchMessageHoldUntilRef = useRef(0);
  
  // 🆕 스크롤 및 하이라이트 함수
  const scrollToModeButtons = useCallback(() => {
    if (modeButtonsRef.current) {
      modeButtonsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // 하이라이트 효과
      modeButtonsRef.current.classList.add('highlight-pulse');
      setTimeout(() => {
        modeButtonsRef.current?.classList.remove('highlight-pulse');
      }, 2000);
    }
  }, []);
  
  // 학생 전환 시 이전 학생의 자동 선택 타이머가 새 학생 상세를 덮어쓰지 않도록 관리
  const violationAutoSelectTimerRef = useRef(null);

  useEffect(() => () => {
    if (violationAutoSelectTimerRef.current) {
      clearTimeout(violationAutoSelectTimerRef.current);
    }
  }, []);

  // 학생 검사 결과 로드 및 첫 번째 위반사항 자동 선택 (공통 함수)
  const loadStudentResult = useCallback((student, studentIndex = null, recordId = null) => {
    if (violationAutoSelectTimerRef.current) {
      clearTimeout(violationAutoSelectTimerRef.current);
      violationAutoSelectTimerRef.current = null;
    }
    // 이전 학생의 선택 상태가 남지 않도록 결과 교체 전에 즉시 초기화
    setSelectedViolation(null);
    const originalContent = student.content || '';
    const studentContent = originalContent || student.refined_sentence || '';
    const sanitizedStudent = originalContent
      ? sanitizeInspectorResult(student, originalContent)
      : {
          ...student,
          basic_violations: [],
          guideline_violations: [],
          error: student.error || 'original_unavailable',
          error_message: student.error_message
            || '저장된 검사 초안이 없어 이 수정안을 다시 검사해주세요.',
        };
    setInputText(studentContent);
    setResult({
      record_id: recordId,
      student_index: studentIndex,
      basic_violations: sanitizedStudent.basic_violations || [],
      guideline_violations: sanitizedStudent.guideline_violations || [],
      bytes: sanitizedStudent.bytes || calculateNeisBytes(studentContent),
      quality_score: sanitizedStudent.quality_score,
      summary: sanitizedStudent.summary,
      academic_year: sanitizedStudent.academic_year || academicYear,
      record_grade: sanitizedStudent.record_grade || recordGrade,
      guideline_file: sanitizedStudent.guideline_file,
      auto_refined_sentence: sanitizedStudent.auto_refined_sentence,
      refined_sentence: sanitizedStudent.refined_sentence,
      refined_bytes: sanitizedStudent.refined_bytes,
      revision_history: sanitizedStudent.revision_history || [],
      error: sanitizedStudent.error,
      error_message: sanitizedStudent.error_message
    });
    resetRefineUi();
    
    // 위반사항 자동 선택
    violationAutoSelectTimerRef.current = setTimeout(() => {
      violationAutoSelectTimerRef.current = null;
      const allViolations = [
        ...(sanitizedStudent.basic_violations || []).map(v => ({ ...v, source: 'basic' })),
        ...(sanitizedStudent.guideline_violations || []).map(v => ({ ...v, source: 'guideline' }))
      ];
      
      if (allViolations.length > 0) {
        const groupedViolations = {};
        allViolations.forEach(v => {
          if (!v.found) return;
          if (!groupedViolations[v.found]) groupedViolations[v.found] = [];
          groupedViolations[v.found].push(v);
        });

        const allOccurrences = [];
        Object.entries(groupedViolations).forEach(([found, violations]) => {
          let searchStart = 0;
          while (true) {
            const foundIndex = studentContent.indexOf(found, searchStart);
            if (foundIndex === -1) break;
            allOccurrences.push({
              found,
              violations,
              index: foundIndex,
              endIndex: foundIndex + found.length
            });
            searchStart = foundIndex + 1;
          }
        });

        allOccurrences.sort((a, b) => a.index - b.index);
        
        if (allOccurrences.length > 0) {
          const firstOccurrence = allOccurrences[0];
          setSelectedViolation({
            isGroup: true,  // 항상 true로 설정 (단일 검사와 동일하게)
            found: firstOccurrence.found,
            index: firstOccurrence.index,
            violations: firstOccurrence.violations,
            count: firstOccurrence.violations.length
          });
        }
      }
    }, 100);
  }, [academicYear, recordGrade, resetRefineUi]);

  useEffect(() => {
    currentResultKeyRef.current = result
      ? `${mode}:${result.record_id || 'unsaved'}:${result.student_index ?? 'single'}`
      : '';
  }, [mode, result]);

  // 위반이 있는 결과에서만 오른쪽 상세 열을 예약 (0건이면 전체 폭 축하 화면)
  const resultHasViolations = Boolean(result) && (
    (result.basic_violations?.length || 0) + (result.guideline_violations?.length || 0) > 0
  );

  useEffect(() => {
    const restoreState = location.state;
    if (!restoreState?.restoreFromRecord || !restoreState.record) return;

    const record = restoreState.record;
    const restoreKey = `${record.id}:${restoreState.studentIndex ?? 0}`;
    if (restoredInspectorRecordRef.current === restoreKey) return;
    restoredInspectorRecordRef.current = restoreKey;

    setSchoolLevel(record.school_level || 'elementary');
    setAcademicYear(normalizeInspectorAcademicYear(
      record.school_level || 'elementary',
      record.cohort_academic_year
        || record.academic_year
        || CURRENT_INSPECTOR_ACADEMIC_YEAR,
    ));
    setRecordGrade(
      isValidInspectorGrade(
        record.school_level || 'elementary',
        record.cohort_grade || record.record_grade,
      )
        ? Number(record.cohort_grade || record.record_grade)
        : '',
    );
    setShowGuidelineContextError(false);
    if (record.selected_model) setSelectedModel(record.selected_model);

    if (record.mode === 'batch') {
      const results = (record.results || []).map(student => {
        const contextualizedStudent = {
          ...student,
          academic_year: student.academic_year || record.academic_year,
          record_grade: student.record_grade || record.record_grade,
          guideline_file: student.guideline_file || record.guideline_file,
        };
        return contextualizedStudent.content
          ? sanitizeInspectorResult(
              contextualizedStudent,
              contextualizedStudent.content,
            )
          : contextualizedStudent;
      });
      const studentIndex = Math.min(
        Math.max(Number(restoreState.studentIndex || 0), 0),
        Math.max(results.length - 1, 0)
      );
      setMode('batch');
      setBatchStep(3);
      setBatchApproximate(false);
      setBatchResults({
        success: true,
        record_id: record.id,
        results,
        summary: record.summary || {}
      });
      setSelectedBatchStudent(studentIndex);
      if (results[studentIndex]) {
        loadStudentResult(results[studentIndex], studentIndex, record.id);
      }
    } else {
      const restoredText = record.input_text || record.refined_sentence || '';
      const sanitizedRecord = record.input_text
        ? sanitizeInspectorResult(record, record.input_text)
        : {
            ...record,
            basic_violations: [],
            guideline_violations: [],
            error: 'original_unavailable',
            error_message: '저장된 검사 초안이 없어 이 수정안을 다시 검사해주세요.',
          };
      setMode('single');
      setInputText(restoredText);
      setResult({
        record_id: record.id,
        basic_violations: sanitizedRecord.basic_violations || [],
        guideline_violations: sanitizedRecord.guideline_violations || [],
        bytes: sanitizedRecord.bytes || calculateNeisBytes(restoredText),
        quality_score: sanitizedRecord.quality_score || 5,
        summary: sanitizedRecord.summary || '',
        academic_year: record.academic_year,
        record_grade: record.record_grade,
        guideline_file: record.guideline_file,
        auto_refined_sentence: sanitizedRecord.auto_refined_sentence,
        refined_sentence: sanitizedRecord.refined_sentence,
        refined_bytes: sanitizedRecord.refined_bytes,
        revision_history: sanitizedRecord.revision_history || [],
        error: sanitizedRecord.error,
        error_message: sanitizedRecord.error_message,
      });
      resetRefineUi();
    }

    setTimeout(() => {
      resultSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
  }, [loadStudentResult, location.state, resetRefineUi]);
  
  // 일괄 검사 성공 결과 공통 처리 (정상 완료 / 재접속 replay / 기록 복구가 모두 사용)
  const finishBatchCheck = useCallback((data) => {
    assertInspectorContextResponse(data, {
      academicYear,
      recordGrade: Number(recordGrade),
    });
    const sanitizedData = {
      ...data,
      results: (data.results || []).map(student => {
        const studentText = student.content || student.refined_sentence || '';
        return sanitizeInspectorResult(student, studentText);
      }),
    };
    // 결과 중 rate limit 에러가 있는지 확인
    const rateLimitErrors = sanitizedData.results?.filter(r => r.error === 'rate_limit_exceeded') || [];
    if (rateLimitErrors.length > 0) {
      // 통일된 Rate Limit 메시지 + 실패 건수
      showToast(`${rateLimitErrors.length}명의 검사가 실패했어요`, 'error');
    }
    const parseErrors = sanitizedData.results?.filter(r => r.error === 'parse_failed' || r.error === 'empty_response') || [];
    if (parseErrors.length > 0) {
      showToast(`${parseErrors.length}명의 검사 결과를 해석하지 못했어요. 해당 학생만 다시 시도해주세요.`, 'error');
    }

    if (sanitizedData.cohort_academic_year || sanitizedData.academic_year) {
      setAcademicYear(Number(
        sanitizedData.cohort_academic_year
        || sanitizedData.academic_year,
      ));
    }
    if (sanitizedData.cohort_grade || sanitizedData.record_grade) {
      setRecordGrade(Number(
        sanitizedData.cohort_grade
        || sanitizedData.record_grade,
      ));
    }
    setBatchResults(sanitizedData);
    setBatchStep(3);
    trackInspectorUse('batch', schoolLevel, isProUser);
    trackRiseFeatureAction('saenggibu_inspect', 'generate', {
      mode: 'batch',
      academicYear: sanitizedData.academic_year || academicYear,
      recordGrade: sanitizedData.record_grade || recordGrade,
    });

    // 첫 번째 학생 자동 선택
    if (sanitizedData.results && sanitizedData.results.length > 0) {
      setSelectedBatchStudent(0);
      loadStudentResult(sanitizedData.results[0], 0, sanitizedData.record_id);
    }
  }, [
    academicYear,
    isProUser,
    loadStudentResult,
    recordGrade,
    schoolLevel,
    showToast,
  ]);

  // 내부 검사 함수 (students를 인자로 받음)
  // 2026-07: 하트비트 SSE 스트림 + 서버 멱등성 + 자동 복구로 전환.
  // 기존 방식은 2~5분 무음 요청이라 학교망이 약 3분에 연결을 끊었고(실측 재전송률 7.8%),
  // 끊기면 같은 검사가 서버에서 중복 실행되면서 사용자는 실패 화면을 봤다.
  const handleBatchCheckInternal = useCallback(async (students) => {
    if (!validateGuidelineContext()) return;
    setBatchLoading(true);
    setBatchStep(2);
    setBatchProgress(0);
    setBatchCheckCounts(null);
    setCurrentChecking('검사 준비 중...');
    batchServerFloorRef.current = 0;
    batchHasServerCountsRef.current = false;
    batchMessageHoldUntilRef.current = 0;

    // 같은 jobId 로 재접속하면 서버가 실행 중인 검사에 합류하거나 저장된 결과를 재사용한다
    const checkJobId = `inspect_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    const totalStudents = students.length;

    // 서버 진행 이벤트가 오기 전까지만 쓰는 완만한 시뮬레이션 (기존 체감 유지)
    const progressDuration = Math.min(360_000, 120_000 + totalStudents * 12_000);
    const startTime = Date.now();

    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const simulated = Math.min(Math.round(10 + (elapsed / progressDuration) * 76), 86);
      const floor = batchServerFloorRef.current;
      // 서버 카운트가 있으면 다음 청크 완료 전까지 +10%p 까지만 살짝 움직인다 (최대 95%)
      const cap = batchHasServerCountsRef.current ? Math.min(floor + 10, 95) : 86;
      setBatchProgress(Math.max(Math.min(simulated, cap), floor));

      // 서버가 실제 진행률 메시지를 보내는 동안에는 시뮬레이션 메시지로 덮지 않는다
      if (batchHasServerCountsRef.current) return;
      if (Date.now() < batchMessageHoldUntilRef.current) return;

      const elapsedSeconds = Math.floor(elapsed / 1000);
      if (elapsedSeconds < 30) {
        setCurrentChecking(`${totalStudents}명의 생기부를 기재요령과 대조하고 있어요`);
      } else if (elapsedSeconds < 120) {
        setCurrentChecking(`${totalStudents}명의 생기부에서 위반 표현과 맞춤법을 확인하고 있어요`);
      } else {
        setCurrentChecking(`${totalStudents}명 검사가 길어지고 있어요. 창을 닫지 않으면 결과가 이어서 표시돼요`);
      }
    }, 500);

    // 서버 진행 이벤트 처리 (실제 청크 단위 완료 인원 = 5명 단위)
    const handleServerProgress = (event) => {
      if (!event) return;
      const stage = event.stage;
      if (event.type === 'start') return;

      if (stage === 'chunk_start' || stage === 'chunk_done') {
        const total = event.total_students || totalStudents;
        const completed = Math.min(event.completed_students || 0, total);
        batchHasServerCountsRef.current = true;
        batchServerFloorRef.current = Math.max(
          batchServerFloorRef.current,
          Math.min(8 + Math.round((completed / Math.max(total, 1)) * 88), 96)
        );
        setBatchCheckCounts({ completed, total });
        if (stage === 'chunk_done') {
          setCurrentChecking(`${total}명 중 ${completed}명 검사 완료`);
        } else {
          const nextCount = Math.min(5, Math.max(total - completed, 0));
          setCurrentChecking(
            completed > 0
              ? `${total}명 중 ${completed}명 완료 · 다음 ${nextCount}명을 검사하고 있어요`
              : `${total}명 중 첫 ${nextCount}명을 검사하고 있어요`
          );
        }
        return;
      }
      if (stage === 'joining') {
        setCurrentChecking('이전 검사 요청이 진행 중이에요. 결과를 기다리고 있어요');
        batchMessageHoldUntilRef.current = Date.now() + 8_000;
        return;
      }
      if (stage === 'reconnecting') {
        setCurrentChecking(`연결이 불안정해요. 자동으로 다시 연결하는 중... (${event.attempt || 2}번째 시도)`);
        batchMessageHoldUntilRef.current = Date.now() + 8_000;
      }
      // heartbeat 는 연결 유지용 → 표시 변경 없음
    };

    // 스트림이 완전히 끊긴 뒤의 마지막 복구: 잡 상태를 폴링해 저장된 결과를 가져온다
    const recoverViaJobStatus = async () => {
      const deadline = Date.now() + 90_000;
      while (Date.now() < deadline) {
        let status = null;
        try {
          status = await getInspectorCheckJobStatus(checkJobId);
        } catch {
          // 상태 조회 실패 → 잠시 후 재시도
        }
        if (status?.status === 'completed' && status.record_id) {
          try {
            const detail = await getInspectorRecord(status.record_id);
            const record = detail?.record;
            if (record?.results?.length) {
              return {
                success: true,
                record_id: status.record_id,
                results: record.results,
                summary: record.summary || {},
                recovered: true,
              };
            }
          } catch {
            // 기록 조회 실패 → 잠시 후 재시도
          }
        }
        if (status?.status === 'failed') return null;
        if (status?.status === 'not_found') return null;
        await new Promise(resolve => setTimeout(resolve, 6_000));
      }
      return null;
    };

    // 배포 시차 대비: 스트림 엔드포인트가 없는 구버전 백엔드면 기존 JSON 경로 사용
    const runLegacyBatchCheck = async () => {
      const token = await currentUser.getIdToken();
      const batchTimeoutMs = calcApiTimeout('batch-inspector', { studentCount: totalStudents });
      const response = await fetchWithRetry(
        getApiUrl('/api/inspector/check-batch'),
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            students,
            school_level: schoolLevel,
            selected_model: selectedModel,
            academic_year: academicYear,
            record_grade: Number(recordGrade),
            check_job_id: checkJobId,
          }),
        },
        { maxRetries: 1, timeoutMs: batchTimeoutMs, retryOnTimeout: false }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || '일괄 검사 중 문제가 생겼어요');
      }
      return data;
    };

    try {
      let data;
      try {
        data = await checkBatchStream({
          students,
          schoolLevel,
          selectedModel,
          academicYear,
          recordGrade: Number(recordGrade),
          jobId: checkJobId,
          onProgress: handleServerProgress,
        });
      } catch (streamError) {
        if (streamError?.isHttpError && streamError.status === 404) {
          data = await runLegacyBatchCheck();
        } else {
          throw streamError;
        }
      }

      clearInterval(progressInterval);

      if (data.success) {
        setBatchProgress(100);
        setCurrentChecking('검사 완료!');
        finishBatchCheck(data);
      } else if (data.cancelled) {
        showToast(data.message || '검사를 중단했어요.', 'info');
        setBatchStep(1);
      } else if (isRateLimitError(data.detail)) {
        // 통일된 Rate Limit 처리 (에러 토스트, 사라지지 않음)
        showRateLimitAlert(showToast);
        setBatchStep(1);
      } else {
        showToast(data.detail || data.message || '일괄 검사 중 문제가 생겼어요', 'error');
        setBatchStep(1);
      }
    } catch (error) {
      clearInterval(progressInterval);
      if (isRateLimitError(error)) {
        // 통일된 Rate Limit 처리 (에러 토스트, 사라지지 않음)
        showRateLimitAlert(showToast);
        setBatchStep(1);
      } else if (error?.isServerReportedError || (error?.isHttpError && error.status < 500)) {
        // 서버가 판단한 실패 → 복구 시도 없이 바로 안내
        showToast(getFriendlyInspectorErrorMessage(error), 'error');
        setBatchStep(1);
      } else {
        // 네트워크성 실패 → 서버에서 검사가 완료되어 저장됐을 수 있으니 결과 복구 시도
        setCurrentChecking('연결이 불안정해요. 완료된 검사 결과가 있는지 확인하고 있어요...');
        const recovered = await recoverViaJobStatus();
        if (recovered) {
          setBatchProgress(100);
          setCurrentChecking('검사 완료!');
          showToast('네트워크가 불안정했지만 검사 결과를 복구했어요.', 'success');
          finishBatchCheck(recovered);
        } else {
          showToast(getFriendlyInspectorErrorMessage(error), 'error');
          setBatchStep(1);
        }
      }
    } finally {
      setBatchLoading(false);
    }
  }, [
    academicYear,
    currentUser,
    finishBatchCheck,
    recordGrade,
    schoolLevel,
    selectedModel,
    showToast,
    validateGuidelineContext,
  ]);

  // 🔍 파일 사전 검사 수행
  const performFilePreCheck = useCallback(async (file) => {
    if (!currentUser) {
      setShowLoginPrompt(true);
      return;
    }
    
    if (!await requireProAccess('inspector-batch')) return;
    
    // 로딩 시작
    setPreCheckLoading(true);
    setShowPreCheckModal(true);
    
    try {
      const result = await preCheckFiles([file], 'inspector', isProUser);
      
      // 검사 통과 & 확인 불필요 시 모달 없이 바로 업로드 진행
      if (result.can_proceed && !result.needs_confirmation) {
        setShowPreCheckModal(false);
        setPreCheckLoading(false);
        actualExcelUploadRef.current(file);  // 🔧 ref를 통해 최신 함수 호출
        return;
      }
      
      // 확인이 필요하거나 진행 불가인 경우에만 모달 표시
      setPreCheckResult(result);
      setPendingFile(file);
      setPreCheckLoading(false);
      
    } catch (error) {
      console.error('파일 사전 검사 오류:', error);
      setPreCheckResult({
        can_proceed: true,
        needs_confirmation: true,
        overall_status: 'warning',
        title: '파일 사전 검사를 확인하지 못했어요',
        message: '파일 내용이 길어서 일부만 분석될 수 있는지 확인하지 못했어요.',
        details: ['네트워크나 서버 상태 때문에 업로드 전 파일 검사를 완료하지 못했어요.'],
        recommendations: ['계속 진행할 수 있지만, 큰 파일이면 앞부분만 분석될 수 있어요.'],
        files_info: [{
          filename: file?.name || '파일',
          status: 'warning',
          file_size: file?.size || 0,
          issues: ['파일 사전 검사 실패']
        }]
      });
      setPendingFile(file);
      setPreCheckLoading(false);
    }
  }, [currentUser, isProUser, requireProAccess]);  // 🔧 의존성 유지 (ref 사용으로 actualExcelUpload 불필요)

  // 🔍 사전 검사 확인 후 업로드 진행
  const handlePreCheckConfirm = useCallback(() => {
    setShowPreCheckModal(false);
    if (pendingFile) {
      actualExcelUploadRef.current(pendingFile);  // 🔧 ref를 통해 최신 함수 호출
    }
    setPendingFile(null);
    setPreCheckResult(null);
  }, [pendingFile]);  // 🔧 업로드 함수 의존성 제거 (ref 사용)

  // 🔍 사전 검사 취소
  const handlePreCheckCancel = useCallback(() => {
    setShowPreCheckModal(false);
    setPendingFile(null);
    setPreCheckResult(null);
  }, []);
  
  // 실제 파일 업로드 처리
  const actualExcelUpload = useCallback(async (file) => {
    if (!validateGuidelineContext()) return;

    if (isClearlyBlockedUploadFile(file)) {
      setUploadError({
        type: 'invalid_format',
        message: '지원하지 않는 파일 형식이에요',
        detail: '코드, 문서, 이미지 파일만 올릴 수 있어요'
      });
      return;
    }
    
    // 📎 파일 크기 체크 (Pro 30MB / Free 30MB)
    const { fileSize: maxSize } = getFileLimits(isProUser);
    const maxSizeMB = Math.round(maxSize / (1024 * 1024));
    if (file.size > maxSize) {
      setUploadError({
        type: 'file_too_large',
        message: '파일이 너무 커요',
        detail: `${maxSizeMB}MB 이하 파일만 올릴 수 있어요`
      });
      return;
    }
    
    setBatchLoading(true);
    setBatchFile(file);
    setBatchApproximate(/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name || ''));
    setUploadProgress(0);
    setUploadRemainingTime(60);
    setUploadError(null); // 이전 에러 초기화
    
    // 프로그레스바: 60초 동안 천천히 올라감 (전체 검사)
    const progressDuration = 60 * 1000; // 60초 (ms)
    // 시간 표시: 60초로 표시
    const displayDuration = 60; // 60초
    const startTime = Date.now();
    
    // 프로그레스 인터벌 시작
    uploadProgressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min(Math.round((elapsed / progressDuration) * 95), 95);
      setUploadProgress(newProgress);
      
      // 남은 시간 계산 (표시용 30초 기준)
      const elapsedSeconds = Math.floor(elapsed / 1000);
      const remaining = Math.max(displayDuration - elapsedSeconds, 0);
      setUploadRemainingTime(remaining);
    }, 500);
    
    try {
      // 토큰 가져오기
      const token = await currentUser.getIdToken();
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('school_level', schoolLevel);
      
      const response = await fetch(getApiUrl('/api/inspector/parse-excel-for-inspection'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      // 완료 시 인터벌 정리
      if (uploadProgressIntervalRef.current) {
        clearInterval(uploadProgressIntervalRef.current);
      }
      setUploadProgress(100);
      setUploadRemainingTime(0);
      
      if (data.success) {
        // setBatchStudents(data.students); // 사용하지 않음 - 제거
        setUploadError(null);
        
        // 🆕 파일 잘림 경고가 있으면 표시
        if (data.warning) {
          setUploadError({
            type: 'warning',
            message: '파일 일부만 분석됨',
            detail: data.warning
          });
          setPendingTruncatedInspection({
            students: data.students,
            warning: data.warning,
            source: 'file'
          });
          return;
        }

        // 🆕 스캔 PDF를 Cloud Vision 행 분할로 읽은 경우: 인식된 학생(학번·이름) 목록을 먼저 확인받는다.
        // 표 양식이 달라 학생이 빠지거나 합쳐졌을 때 검사 전에 잡을 수 있게.
        if (typeof data.parser === 'string' && data.parser.startsWith('cloud_vision')) {
          setBatchApproximate(true);
          setPendingScanPreview({
            students: data.students,
            meta: data.scan_meta || {},
          });
          return;
        }
        
        // Step 2 (확인/수정) 생략하고 바로 검사 시작
        setTimeout(() => {
          handleBatchCheckInternal(data.students);
        }, 300);
      } else {
        // 백엔드에서 success: false 반환 시
        throw new Error(data.detail || '파일을 분석하지 못했어요. 다시 시도해 주세요.');
      }
    } catch (error) {
      // 에러 시 인터벌 정리
      if (uploadProgressIntervalRef.current) {
        clearInterval(uploadProgressIntervalRef.current);
      }
      console.error('엑셀 파싱 오류:', error);
      
      // 에러 타입에 따라 상세 메시지 설정
      let errorType = 'unknown';
      let errorMessage = '파일 분석 중 문제가 생겼어요';
      let errorDetail = '';
      
      if (error.response) {
        // HTTP 에러 응답
        const status = error.response.status;
        const serverError = error.response.data?.detail || error.message;
        
        if (status === 400) {
          errorType = 'invalid_data';
          errorMessage = '파일 내용을 확인해주세요';
          errorDetail = serverError || '학생명과 생기부 내용이 포함되어 있어야 해요';
          
          // AI가 학생을 찾지 못한 경우 특별 처리
          if (serverError && serverError.includes('학생 정보를 찾을 수 없습니다')) {
            errorType = 'no_students_found';
            errorMessage = 'AI가 학생 정보를 찾지 못했어요';
          }
        } else if (status === 500) {
          errorType = 'server_error';
          errorMessage = 'AI 분석 중 문제가 생겼어요';
          errorDetail = serverError || '잠시 후 다시 시도해주세요';
        } else if (status === 413) {
          errorType = 'file_too_large';
          errorMessage = '파일이 너무 커요';
          const maxMb = Math.round(getFileLimits(isProUser).fileSize / (1024 * 1024));
          errorDetail = `${maxMb}MB 이하 파일로 다시 올려주세요`;
        } else {
          errorDetail = serverError || '서버 연결에 문제가 있어요';
        }
      } else if (error.message.includes('Network')) {
        errorType = 'network_error';
        errorMessage = '인터넷 연결을 확인해주세요';
        errorDetail = '네트워크 연결 후 다시 시도해주세요';
      } else {
        errorDetail = error.message || '알 수 없는 문제가 생겼어요. 파일을 확인하고 다시 시도해주세요';
      }
      
      setUploadError({
        type: errorType,
        message: errorMessage,
        detail: errorDetail
      });
      
      setBatchFile(null);
    } finally {
      setBatchLoading(false);
    }
  }, [
    currentUser,
    handleBatchCheckInternal,
    isProUser,
    schoolLevel,
    validateGuidelineContext,
  ]);
  
  // 🔧 ref 업데이트 (최신 함수 참조 유지)
  actualExcelUploadRef.current = actualExcelUpload;

  const handleDownloadInspectorTemplate = useCallback(() => {
    const ws = XLSX.utils.aoa_to_sheet(INSPECTOR_TEMPLATE_ROWS);
    ws['!cols'] = [{ wch: 8 }, { wch: 12 }, { wch: 90 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '생기부검사');
    XLSX.writeFile(wb, '생기부검사_참고양식.xlsx');
    showToast('검사용 참고 양식을 다운로드했어요', 'success');
  }, [showToast]);

  const handleConfirmInspectorTemplateDownload = useCallback(() => {
    setShowInspectorTemplateGuide(false);
    handleDownloadInspectorTemplate();
  }, [handleDownloadInspectorTemplate]);

  const closeInspectorAssistGuide = useCallback(() => {
    if (inspectorAssistDetailsRef.current) {
      inspectorAssistDetailsRef.current.open = false;
    }
  }, []);

  const handleFillInspectorExample = useCallback(() => {
    closeInspectorAssistGuide();
    const exampleText = INSPECTOR_TEMPLATE_ROWS.map(row => row.join('\t')).join('\n');
    setBatchPasteRows(buildInspectorGridRowsFromText(exampleText));
    setBatchPasteText(exampleText);
    setBatchPasteError(null);
    setUploadError(null);
    showToast('예시를 붙여넣기 칸에 채웠어요', 'success');
  }, [closeInspectorAssistGuide, showToast]);

  const handleInspectorGridPaste = useCallback((e) => {
    const clipboardText = e.clipboardData?.getData('text/plain') || '';
    if (!clipboardText.trim()) return;
    if (!shouldHydrateInspectorGridFromClipboard(clipboardText)) return;

    e.preventDefault();
    const nextRows = buildInspectorGridRowsFromText(clipboardText);
    setBatchPasteRows(nextRows);
    setBatchPasteText(inspectorGridRowsToText(nextRows));
    setBatchPasteError(null);
  }, []);

  const handleInspectorCellChange = useCallback((rowIndex, field, value) => {
    setBatchPasteRows(prevRows => {
      const nextRows = prevRows.map((row, index) => (
        index === rowIndex ? { ...row, [field]: value } : row
      ));
      setBatchPasteText(inspectorGridRowsToText(nextRows));
      return nextRows;
    });
    setBatchPasteError(null);
  }, []);

  const handleAddInspectorPasteRow = useCallback(() => {
    setBatchPasteRows(prevRows => ([
      ...prevRows,
      { no: String(prevRows.length + 1), name: '', content: '' }
    ]));
  }, []);

  const handleAnalyzeInspectorPaste = useCallback(async () => {
    if (!validateGuidelineContext()) return;
    if (!await requireProAccess('inspector-batch')) return;

    const textFromGrid = inspectorGridRowsToText(batchPasteRows);
    const trimmedText = (textFromGrid || batchPasteText).trim();
    if (!trimmedText) {
      setBatchPasteError('엑셀에서 복사한 표나 생기부 내용을 붙여넣어주세요.');
      return;
    }

    try {
      setBatchPasteLoading(true);
      setBatchPasteError(null);
      setUploadError(null);
      setBatchApproximate(false);

      const parsedStudents = parseInspectionStudentsFromPastedTable(trimmedText);
      if (parsedStudents.length > 0) {
        await handleBatchCheckInternal(parsedStudents);
        return;
      }

      const token = await currentUser.getIdToken();
      const response = await fetchWithRetry(
        getApiUrl('/api/inspector/parse-pasted-for-inspection'),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            text: trimmedText,
            school_level: schoolLevel,
          }),
        },
        { maxRetries: 1, timeoutMs: calcApiTimeout('batch-inspector', { studentCount: 10 }) }
      );

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.detail || '붙여넣은 내용을 분석하지 못했어요.');
      }

      const students = data.students || [];
      if (students.length === 0) {
        throw new Error('학생명과 생기부 내용을 찾지 못했어요. 이름과 완성된 문장이 함께 보이도록 붙여넣어주세요.');
      }

      if (data.warning) {
        setUploadError({
          type: 'warning',
          message: '붙여넣은 내용 일부만 분석됨',
          detail: data.warning,
        });
        setPendingTruncatedInspection({
          students,
          warning: data.warning,
          source: 'paste'
        });
        return;
      }

      await handleBatchCheckInternal(students);
    } catch (error) {
      console.error('검사기 붙여넣기 분석 오류:', error);
      setBatchPasteError(error.message || '붙여넣은 내용을 분석하지 못했어요.');
    } finally {
      setBatchPasteLoading(false);
    }
  }, [
    batchPasteRows,
    batchPasteText,
    currentUser,
    handleBatchCheckInternal,
    requireProAccess,
    schoolLevel,
    validateGuidelineContext,
  ]);
  
  // 드래그 앤 드롭 핸들러
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);
  
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      performFilePreCheck(files[0]);
    }
  }, [performFilePreCheck]);
  
  // 전체 수정 제안 문장 생성 (basic + guideline 모두 반영)
  const generateCorrectedText = useCallback(() => {
    // 모든 위반사항 통합
    const allViolations = [
      ...(result?.basic_violations || []),
      ...(result?.guideline_violations || [])
    ];
    
    if (allViolations.length === 0) {
      return inputText;
    }
    
    let correctedText = inputText;
    
    // 뒤에서부터 교체 (인덱스 변화 방지)
    const violations = [...allViolations].sort((a, b) => {
      const indexA = correctedText.indexOf(a.found);
      const indexB = correctedText.indexOf(b.found);
      return indexB - indexA;
    });
    
    violations.forEach((violation) => {
      if (violation.found && violation.suggestion !== undefined) {
        const foundIndex = correctedText.indexOf(violation.found);
        if (foundIndex !== -1) {
          // 단순 교체 (found를 suggestion으로, 빈 문자열도 허용 = 삭제)
          const before = correctedText.substring(0, foundIndex);
          const after = correctedText.substring(foundIndex + violation.found.length);
          correctedText = before + violation.suggestion + after;
        }
      }
    });
    
    // 마지막 정리 (중복 문자 제거)
    correctedText = correctedText
      .replace(/\s{2,}/g, ' ')      // 중복 공백 → 1개
      .replace(/\.{2,}/g, '.')      // 중복 마침표 → 1개
      .replace(/,{2,}/g, ',')       // 중복 쉼표 → 1개
      .replace(/\s+\./g, '.')       // 공백 + 마침표 → 마침표
      .replace(/\s+,/g, ',')        // 공백 + 쉼표 → 쉼표
      .trim();
    
    return correctedText;
  }, [inputText, result]);
  
  // 검사 실행
  const handleCheck = useCallback(async () => {
    if (!inputText.trim()) {
      showToast('검사할 문장을 입력해주세요', 'warning');
      return;
    }
    if (!validateGuidelineContext()) return;
    
    // 비로그인 사용자: 1회차 허용, 2회차부터 가입 필수
    if (isAnonymousUser) {
      localStorage.setItem('inspector-saved-text', inputText);
      localStorage.setItem('inspector-saved-school', schoolLevel);
      localStorage.setItem('inspector-saved-academic-year', String(academicYear));
      localStorage.setItem('inspector-saved-record-grade', String(recordGrade));
      if (anonCheckCount >= 1) {
        setLoginPromptFeature('inspector');
        setShowLoginPrompt(true);
        return;
      }
    }
    
    setLoading(true);
    setResult(null);
    setSelectedViolation(null);
    resetRefineUi();
    
    try {
      // 고급 모드 선택 + 무료 유저 체크
      if (selectedModel === 'gemini-2.5-flash' && !isProUser) {
        const hasProAccess = await requireProAccess('inspector');
        if (!hasProAccess) {
          setLoading(false);
          return;
        }
      }
      
      const rawData = await checkSingleSentence(
        inputText,
        schoolLevel,
        selectedModel,
        academicYear,
        Number(recordGrade),
      );
      const data = sanitizeInspectorResult(rawData, inputText);
      setResult(data);
      trackInspectorUse('single', schoolLevel, isProUser);
      trackRiseFeatureAction('saenggibu_inspect', 'generate', {
        mode: 'single',
        academicYear,
        recordGrade: Number(recordGrade),
      });
      
      // 비로그인 사용자 체험 카운터 증가
      if (isAnonymousUser) {
        const newCount = anonCheckCount + 1;
        setAnonCheckCount(newCount);
        sessionStorage.setItem('inspector-anon-check-count', String(newCount));
      }
      
      // 모든 위반사항 통합 (basic + guideline)
      const allViolations = [
        ...(data.basic_violations || []).map(v => ({ ...v, source: 'basic' })),
        ...(data.guideline_violations || []).map(v => ({ ...v, source: 'guideline' }))
      ];
      
      // 🎉 완벽 체크 (위반사항이 0개인 경우)
      const totalViolations = allViolations.length;
      if (totalViolations === 0) {
        // 랜덤 색종이 효과 선택
        const config = getRandomConfettiConfig();
        setConfettiConfig(config);
        
        // 축하 애니메이션 트리거
        setShowPerfectCelebration(true);
        
        // 5초 후 Confetti 종료
        setTimeout(() => {
          setShowPerfectCelebration(false);
          setConfettiConfig(null);
        }, 5000);
      } else {
        setShowPerfectCelebration(false);
        setConfettiConfig(null);
      }
      
      // 첫 번째 위반사항 그룹 자동 선택 (병합 로직 포함)
      if (allViolations.length > 0) {
        // 1. 그룹화 (빈 found 제외)
        const groupedViolations = {};
        allViolations.forEach(v => {
          if (!v.found) return;
          
          if (!groupedViolations[v.found]) {
            groupedViolations[v.found] = [];
          }
          groupedViolations[v.found].push(v);
        });
        
        // 2. 모든 등장 위치 찾기
        const allOccurrences = [];
        Object.entries(groupedViolations).forEach(([found, violations]) => {
          let searchStart = 0;
          while (true) {
            const foundIndex = inputText.indexOf(found, searchStart);
            if (foundIndex === -1) break;
            
            allOccurrences.push({
              found,
              violations,
              index: foundIndex,
              endIndex: foundIndex + found.length
            });
            
            searchStart = foundIndex + 1;
          }
        });
        
        // 3. 등장 위치별로 정렬
        allOccurrences.sort((a, b) => a.index - b.index);
        
        // 4. 겹치는 영역 병합 (렌더링과 동일한 로직)
        const mergedOccurrences = [];
        let currentMerged = null;
        
        allOccurrences.forEach((occurrence) => {
          if (!currentMerged) {
            currentMerged = {
              index: occurrence.index,
              endIndex: occurrence.endIndex,
              found: occurrence.found,
              violations: [...occurrence.violations]
            };
          } else if (occurrence.index <= currentMerged.endIndex) {
            // 겹치거나 인접한 영역 → 병합
            currentMerged.endIndex = Math.max(currentMerged.endIndex, occurrence.endIndex);
            if (occurrence.found.length > currentMerged.found.length) {
              currentMerged.found = occurrence.found;
            }
            currentMerged.violations.push(...occurrence.violations);
          } else {
            mergedOccurrences.push(currentMerged);
            currentMerged = {
              index: occurrence.index,
              endIndex: occurrence.endIndex,
              found: occurrence.found,
              violations: [...occurrence.violations]
            };
          }
        });
        
        if (currentMerged) {
          mergedOccurrences.push(currentMerged);
        }
        
        // 5. 첫 번째 병합된 영역 자동 선택
        if (mergedOccurrences.length > 0) {
          const firstMerged = mergedOccurrences[0];
          setSelectedViolation({
            isGroup: true,
            found: firstMerged.found,
            index: firstMerged.index,
            violations: firstMerged.violations,
            count: firstMerged.violations.length
          });
        }
      }
      
      // 검사 완료 후 결과 영역으로 자동 스크롤
      setTimeout(() => {
        if (resultSectionRef.current) {
          resultSectionRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
        
        // 모바일에서 위반사항 상세 패널로도 스크롤 (위반사항이 있는 경우)
        const totalViolations = (data.basic_violations?.length || 0) + (data.guideline_violations?.length || 0);
        if (totalViolations > 0 && detailPanelRef.current && window.innerWidth <= 768) {
          setTimeout(() => {
            detailPanelRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }, 500);
        }
      }, 300);
    } catch (error) {
      showToast(error.message || '검사 중 문제가 생겼어요', 'error');
    } finally {
      setLoading(false);
    }
  }, [
    academicYear,
    anonCheckCount,
    inputText,
    isAnonymousUser,
    isProUser,
    recordGrade,
    requireProAccess,
    resetRefineUi,
    schoolLevel,
    selectedModel,
    showToast,
    validateGuidelineContext,
  ]);
  
  // 복사 기능
  const handleCopy = (text, onCopied = null) => {
    navigator.clipboard.writeText(text).then(() => {
      showToast('복사했어요', 'success');
      onCopied?.();
    }).catch(err => {
      showToast('복사하지 못했어요', 'error');
    });
  };

  // 교사 반응 측정용 이벤트. 학생 이름·본문·교사 지시문은 보내지 않는다.
  const trackInspectorAction = useCallback((eventType, extra = {}) => {
    trackEvent(eventType, {
      record_id: result?.record_id || null,
      mode: mode === 'batch' ? 'batch' : 'single',
      student_index: mode === 'batch' ? (result?.student_index ?? null) : null,
      school_level: schoolLevel,
      ...extra,
    });
  }, [mode, result, schoolLevel]);

  const getSuggestionText = useCallback(() => {
    return result?.refined_sentence || result?.auto_refined_sentence || generateCorrectedText();
  }, [generateCorrectedText, result]);

  const updateCurrentInspectorSuggestion = useCallback((content, revision = null) => {
    setResult(prev => prev ? {
      ...prev,
      refined_sentence: content,
      refined_bytes: calculateNeisBytes(content),
      revision_history: revision
        ? [...(prev.revision_history || []), revision].slice(-30)
        : (prev.revision_history || [])
    } : prev);

    if (mode === 'batch' && batchResults?.results?.[selectedBatchStudent]) {
      setBatchResults(prev => {
        if (!prev?.results?.[selectedBatchStudent]) return prev;
        const nextResults = [...prev.results];
        nextResults[selectedBatchStudent] = {
          ...nextResults[selectedBatchStudent],
          refined_sentence: content,
          refined_bytes: calculateNeisBytes(content),
          revision_history: revision
            ? [...(nextResults[selectedBatchStudent].revision_history || []), revision].slice(-30)
            : (nextResults[selectedBatchStudent].revision_history || [])
        };
        return { ...prev, results: nextResults };
      });
    }
  }, [batchResults, mode, selectedBatchStudent]);

  const persistInspectorSuggestion = useCallback(async (content, revision = null) => {
    if (!currentUser || !result?.record_id) {
      return false;
    }

    if (mode === 'batch' && result.student_index !== null && result.student_index !== undefined) {
      await updateBatchInspectorResult(result.record_id, result.student_index, content, revision);
      return true;
    }

    await updateInspectorRecord(result.record_id, content, revision);
    return true;
  }, [currentUser, mode, result]);

  const handleRequestRefine = useCallback(async (preset = null) => {
    if (!result) return;
    const requestKey = `${mode}:${result.record_id || 'unsaved'}:${result.student_index ?? 'single'}`;
    const selectedPreset = preset || INSPECTOR_REFINE_PRESETS.find(item => item.mode === refineMode);
    const modeValue = selectedPreset?.mode || refineMode || 'custom';
    const instruction = (refineInstruction || selectedPreset?.instruction || '').trim();
    const currentContent = getSuggestionText();
    const targetBytes = modeValue === 'reduce_bytes'
      ? Math.max(100, Math.min(calculateNeisBytes(currentContent), result.bytes || calculateNeisBytes(currentContent)))
      : undefined;

    trackInspectorAction('inspector_refine_request', { revision_mode: modeValue });
    try {
      setRefineLoading(true);
      setRefinePanelOpen(true);
      const response = await refineInspectorContent({
        record_id: result.record_id,
        student_index: result.student_index,
        current_content: currentContent,
        original_text: inputText,
        instruction,
        mode: modeValue,
        scope: mode === 'batch' ? 'batch_student' : 'single',
        school_level: schoolLevel,
        academic_year: academicYear,
        record_grade: Number(recordGrade),
        basic_violations: result.basic_violations || [],
        guideline_violations: result.guideline_violations || [],
        target_bytes: targetBytes,
        revision_history: result.revision_history || [],
        selected_model: selectedModel
      });

      setRefineMode(modeValue);
      const nextContent = response.content || '';
      const revision = response.revision || {
        mode: modeValue,
        instruction,
        before_bytes: calculateNeisBytes(currentContent),
        after_bytes: calculateNeisBytes(nextContent)
      };

      if (currentResultKeyRef.current !== requestKey) {
        return;
      }

      setRefinePreview({
        before: currentContent,
        after: nextContent,
        byteCount: response.byte_count || calculateNeisBytes(nextContent),
        revision
      });
      setRefinePreviewDraft(nextContent);
      showToast('AI가 새 수정안을 만들었어요', 'success');
    } catch (error) {
      console.error('검사 결과 AI 수정 실패:', error);
      showToast(error.message || '수정안을 다시 만들지 못했어요', 'error');
    } finally {
      setRefineLoading(false);
    }
  }, [
    academicYear,
    getSuggestionText,
    inputText,
    mode,
    refineInstruction,
    refineMode,
    recordGrade,
    result,
    schoolLevel,
    selectedModel,
    showToast,
    trackInspectorAction
  ]);

  const handleApplySuggestion = useCallback(async (content, revision = null) => {
    if (!content?.trim()) return;
    const finalContent = content.trim();
    const applyEvent = {
      revision_mode: revision?.mode || null,
      adjusted: Boolean(revision?.manually_adjusted_preview),
    };
    updateCurrentInspectorSuggestion(finalContent, revision);
    try {
      setSavingRefine(true);
      const saved = await persistInspectorSuggestion(finalContent, revision);
      trackInspectorAction('inspector_suggestion_apply', { ...applyEvent, saved });
      if (saved) {
        showToast('수정안을 적용하고 기록에 저장했어요', 'success');
      } else {
        showToast('화면에는 반영했어요. 로그인하면 기록에도 저장할 수 있어요', 'warning', 6000);
      }
      setRefinePreview(null);
      setRefinePreviewDraft('');
      setManualEditOpen(false);
      setManualDraft('');
    } catch (error) {
      console.error('검사 수정안 저장 실패:', error);
      trackInspectorAction('inspector_suggestion_apply', { ...applyEvent, saved: false });
      showToast('화면에는 반영했어요. 기록 저장은 다시 시도해주세요', 'warning', 6000);
    } finally {
      setSavingRefine(false);
    }
  }, [persistInspectorSuggestion, showToast, trackInspectorAction, updateCurrentInspectorSuggestion]);

  const renderInspectorSuggestion = () => {
    if (!result) return null;
    const totalViolations = (result.basic_violations?.length || 0) + (result.guideline_violations?.length || 0);
    if (totalViolations === 0) return null;

    const suggestionText = getSuggestionText();
    const suggestionBytes = calculateNeisBytes(suggestionText);
    const byteDiff = suggestionBytes - (result.bytes || 0);

    return (
      <div className="inspector-suggestion-section inspector-refine-card">
        <div className="inspector-suggestion-header">
          <div>
            <h3>AI 수정안</h3>
            <p className="inspector-refine-subtitle">기재요령 위반을 반영해 바로 쓸 수 있게 다듬었어요</p>
          </div>
          <button
            className="inspector-copy-button"
            onClick={() => handleCopy(suggestionText, () => trackInspectorAction('inspector_suggestion_copy'))}
          >
            복사
          </button>
        </div>

        <div className="inspector-suggestion-text">
          {suggestionText}
        </div>

        <div className="inspector-suggestion-footer">
          <span className="inspector-byte-badge">{suggestionBytes} 바이트</span>
          <span className="inspector-byte-diff">
            ({byteDiff > 0 ? '+' : ''}{byteDiff} 바이트)
          </span>
        </div>

        <div className="inspector-refine-actions">
          <button
            type="button"
            className="inspector-refine-secondary"
            onClick={() => {
              setManualEditOpen(prev => !prev);
              setManualDraft(suggestionText);
            }}
          >
            직접 고치기
          </button>
          <button
            type="button"
            className="inspector-refine-secondary"
            onClick={() => {
              setRefinePanelOpen(prev => !prev);
              setManualEditOpen(false);
            }}
          >
            AI에게 다시 요청
          </button>
        </div>

        {manualEditOpen && (
          <div className="inspector-refine-edit-panel">
            <label>직접 고칠 내용</label>
            <textarea
              value={manualDraft}
              onChange={(e) => setManualDraft(e.target.value)}
              rows={5}
            />
            <div className="inspector-refine-panel-footer">
              <span>{calculateNeisBytes(manualDraft)} 바이트</span>
              <button
                type="button"
                className="inspector-refine-primary"
                disabled={savingRefine || !manualDraft.trim()}
                onClick={() => handleApplySuggestion(manualDraft, {
                  mode: 'manual_edit',
                  instruction: '교사가 직접 수정안을 편집함',
                  before_bytes: suggestionBytes,
                  after_bytes: calculateNeisBytes(manualDraft),
                })}
              >
                적용하기
              </button>
            </div>
          </div>
        )}

        {refinePanelOpen && (
          <div className="inspector-refine-edit-panel">
            <label>AI에게 어떻게 다듬을까요?</label>
            <div className="inspector-refine-presets">
              {INSPECTOR_REFINE_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  className={`inspector-refine-preset ${refineMode === preset.mode ? 'active' : ''}`}
                  disabled={refineLoading}
                  onClick={() => {
                    setRefineMode(preset.mode);
                    setRefineInstruction(preset.instruction);
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <textarea
              value={refineInstruction}
              onChange={(e) => setRefineInstruction(e.target.value)}
              placeholder="예: 원문 내용은 유지하고 표현만 더 자연스럽게 해줘"
              rows={3}
            />
            <div className="inspector-refine-panel-footer">
              <span>{refineLoading ? 'AI가 다시 다듬고 있어요' : '요청을 구체적으로 적어도 좋아요'}</span>
              <button
                type="button"
                className="inspector-refine-primary"
                disabled={refineLoading}
                onClick={() => handleRequestRefine()}
              >
                {refineLoading ? '요청 중...' : '요청하기'}
              </button>
            </div>
          </div>
        )}

        {refinePreview && (
          <div className="inspector-refine-preview">
            <div className="inspector-refine-preview-header">
              <strong>새 수정안</strong>
              <span>{calculateNeisBytes(refinePreviewDraft)} 바이트</span>
            </div>
            <textarea
              className="inspector-refine-preview-textarea"
              value={refinePreviewDraft}
              onChange={(e) => setRefinePreviewDraft(e.target.value)}
              rows={5}
            />
            <div className="inspector-refine-actions">
              <button
                type="button"
                className="inspector-refine-primary"
                disabled={savingRefine || !refinePreviewDraft.trim()}
                onClick={() => handleApplySuggestion(refinePreviewDraft, {
                  ...(refinePreview.revision || {}),
                  after_bytes: calculateNeisBytes(refinePreviewDraft),
                  manually_adjusted_preview: refinePreviewDraft !== refinePreview.after,
                })}
              >
                이 수정안 적용
              </button>
              <button
                type="button"
                className="inspector-refine-secondary"
                onClick={() => {
                  setRefinePreview(null);
                  setRefinePreviewDraft('');
                }}
              >
                취소
              </button>
            </div>
          </div>
        )}

      </div>
    );
  };
  
  const inspectorOverlapPlan = useMemo(
    () => (batchResults?.results?.length ? planInspectorOverlap(batchResults.results, { approximate: batchApproximate }) : null),
    [batchApproximate, batchResults],
  );
  const overlapTargetByIndex = useMemo(
    () => new Map((inspectorOverlapPlan?.targets || []).map((target) => [target.index, target])),
    [inspectorOverlapPlan],
  );
  const handleOverlapShown = useCallback((plan) => {
    trackEvent('inspector_class_check_shown', {
      record_id: batchResults?.record_id || null,
      mode: 'batch',
      school_level: schoolLevel,
      approximate: batchApproximate,
      student_count: plan.summary.studentCount,
      flagged_count: plan.targets.length,
      same_count: plan.summary.sameGroups,
      near_count: plan.summary.nearGroups,
      max_people: plan.summary.maxPeople,
      level: plan.summary.severity,
    });
  }, [batchApproximate, batchResults?.record_id, schoolLevel]);

  // 엑셀 다운로드 기능
  const handleExcelDownload = useCallback(() => {
    if (!batchResults || !batchResults.results) return;
    
    // NEIS 바이트 계산 함수
    const calculateBytes = (text) => {
      if (!text) return 0;
      let bytes = 0;
      for (const char of text) {
        if (char === '\n') bytes += 2;
        else if (char.charCodeAt(0) < 128) bytes += 1;
        else bytes += 3;
      }
      return bytes;
    };
    
    const excelData = batchResults.results.map((student, idx) => {
      const refinedText = student.refined_sentence || student.content;
      return {
        '번호': idx + 1,
        '학생명': student.student_name,
        '작성 학년도': student.academic_year || batchResults.academic_year || academicYear,
        '작성 당시 학년': student.record_grade || batchResults.record_grade || recordGrade,
        '수정 후 생기부': refinedText,
        '수정 후 바이트': calculateBytes(refinedText),
        '겹침': overlapExcelCell(inspectorOverlapPlan, idx)
      };
    });
    
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    // 컬럼 너비 설정
    ws['!cols'] = [
      { wch: 6 },   // 번호
      { wch: 12 },  // 학생명
      { wch: 14 },  // 작성 학년도
      { wch: 14 },  // 작성 당시 학년
      { wch: 80 },  // 수정 후 생기부 (넓게)
      { wch: 12 },  // 수정 후 바이트
      { wch: 40 }   // 겹침
    ];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '검사 결과');
    
    const today = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `생기부검사결과_${today}.xlsx`);
    trackEvent('inspector_excel_download', {
      source: 'inspector',
      record_id: batchResults.record_id || null,
      mode: 'batch',
      student_count: batchResults.results.length,
      school_level: schoolLevel,
      overlap_count: inspectorOverlapPlan?.targets?.length || 0,
    });
  }, [academicYear, batchResults, inspectorOverlapPlan, recordGrade, schoolLevel]);
  
  // 텍스트에서 위반사항 하이라이트
  const renderHighlightedText = () => {
    // 모든 위반사항 통합
    const allViolations = filterLocatedInspectorViolations([
      ...(result?.basic_violations || []).map(v => ({ ...v, source: 'basic' })),
      ...(result?.guideline_violations || []).map(v => ({ ...v, source: 'guideline' }))
    ], inputText);
    
    if (allViolations.length === 0) {
      return <div className="inspector-text-display">{inputText}</div>;
    }
    
    // 1. 같은 found 텍스트끼리 그룹화 (빈 문자열 제외)
    const groupedViolations = {};
    allViolations.forEach(v => {
      // 빈 found는 하이라이트할 수 없으므로 제외
      if (!v.found) return;
      
      if (!groupedViolations[v.found]) {
        groupedViolations[v.found] = [];
      }
      groupedViolations[v.found].push(v);
    });
    
    // 2. 각 found 텍스트의 모든 등장 위치 찾기
    const allOccurrences = [];
    Object.entries(groupedViolations).forEach(([found, violations]) => {
      let searchStart = 0;
      while (true) {
        const foundIndex = inputText.indexOf(found, searchStart);
        if (foundIndex === -1) break;
        
        allOccurrences.push({
          found,
          violations,
          index: foundIndex,
          endIndex: foundIndex + found.length
        });
        
        searchStart = foundIndex + 1; // 다음 등장 위치 검색
      }
    });
    
    // 3. 등장 위치별로 정렬
    allOccurrences.sort((a, b) => a.index - b.index);
    
    // 4. 겹치는 영역 병합
    const mergedOccurrences = [];
    let currentMerged = null;
    
    allOccurrences.forEach((occurrence) => {
      if (!currentMerged) {
        // 첫 영역
        currentMerged = {
          index: occurrence.index,
          endIndex: occurrence.endIndex,
          found: occurrence.found,
          violations: [...occurrence.violations]
        };
      } else if (occurrence.index <= currentMerged.endIndex) {
        // 겹치거나 인접한 영역 → 병합
        currentMerged.endIndex = Math.max(currentMerged.endIndex, occurrence.endIndex);
        // 더 긴 found 텍스트 사용
        if (occurrence.found.length > currentMerged.found.length) {
          currentMerged.found = occurrence.found;
        }
        // 위반사항 추가
        currentMerged.violations.push(...occurrence.violations);
      } else {
        // 겹치지 않음 → 이전 병합 영역 저장하고 새로 시작
        mergedOccurrences.push(currentMerged);
        currentMerged = {
          index: occurrence.index,
          endIndex: occurrence.endIndex,
          found: occurrence.found,
          violations: [...occurrence.violations]
        };
      }
    });
    
    // 마지막 병합 영역 저장
    if (currentMerged) {
      mergedOccurrences.push(currentMerged);
    }
    
    let lastIndex = 0;
    const parts = [];
    
    mergedOccurrences.forEach((occurrence, idx) => {
      const foundIndex = occurrence.index;
      
      if (foundIndex !== -1) {
        // 일반 텍스트
        if (foundIndex > lastIndex) {
          parts.push(
            <span key={`text-${idx}`}>
              {inputText.substring(lastIndex, foundIndex)}
            </span>
          );
        }
        
        // 4. 그룹에서 색상 결정 (category 우선, 없으면 severity 기반)
        const getViolationColor = (violations) => {
          // category가 있는 첫 번째 위반 찾기
          const withCategory = violations.find(v => v.category && CATEGORY_COLORS[v.category]);
          if (withCategory) {
            return CATEGORY_COLORS[withCategory.category];
          }
          // category 없으면 기존 severity 기반
          const maxSeverity = Math.max(...violations.map(v => 
            VIOLATION_SEVERITY[v.type] || 0
          ));
          return SEVERITY_COLORS[maxSeverity] || '#3b82f6';
        };
        const color = getViolationColor(occurrence.violations);
        
        // 5. 선택 여부 확인 (이 특정 등장 위치가 선택되었는지)
        const isSelected = selectedViolation && 
          selectedViolation.isGroup &&
          selectedViolation.found === occurrence.found &&
          selectedViolation.index === occurrence.index;
        
        // 6. 하이라이트된 텍스트 (배지 포함)
        parts.push(
          <span
            key={`violation-${idx}`}
            className={`inspector-highlight ${isSelected ? 'selected' : ''}`}
            style={{
              backgroundColor: color,
              opacity: isSelected ? 1 : 0.6,
              position: 'relative',
              display: 'inline-block'
            }}
            onClick={() => {
              // 이 특정 등장 위치를 선택
              setSelectedViolation({
                isGroup: true,
                found: occurrence.found,
                index: occurrence.index,
                violations: occurrence.violations,
                count: occurrence.violations.length
              });
              
              // 모바일에서 자동 스크롤
              setTimeout(() => {
                if (detailPanelRef.current && window.innerWidth <= 768) {
                  detailPanelRef.current.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start' 
                  });
                }
              }, 100);
            }}
            title={`${occurrence.violations.length}개 위반: ${occurrence.violations.map(v => v.type).join(', ')}`}
          >
            {occurrence.found}
            {occurrence.violations.length > 1 && (
              <span className="inspector-violation-badge">
                {occurrence.violations.length}
              </span>
            )}
          </span>
        );
        
        lastIndex = occurrence.endIndex;
      }
    });
    
    // 남은 텍스트
    if (lastIndex < inputText.length) {
      parts.push(
        <span key="text-end">
          {inputText.substring(lastIndex)}
        </span>
      );
    }
    
    return <div className="inspector-text-display">{parts}</div>;
  };
  
  return (
    <div className={`inspector-container${!result && mode === 'single' ? ' inspector-container--narrow' : ''}`} style={{ position: 'relative' }}>
      {/* 🎉 Confetti 색종이 효과 */}
      {showPerfectCelebration && confettiConfig && (
        <Confetti
          width={width}
          height={height}
          {...confettiConfig}
        />
      )}
      
      {/* 헤더 + 모드 버튼 (같은 줄로 컴팩트화 - CreateSaenggibu와 통일) */}
      <div className="inspector-page-header">
        <div className="inspector-header-row">
          <h1 className="inspector-page-title">생기부 검사기</h1>
          <div 
            ref={modeButtonsRef}
            className="inspector-mode-toggle"
          >
            <button
              onClick={() => {
                setMode('single');
                setBatchStep(1);
                setBatchResults(null);
              }}
              className={`inspector-mode-btn ${mode === 'single' ? 'inspector-mode-btn--active-single' : ''}`}
            >
              단일 검사
            </button>
            <button
              onClick={() => {
                setMode('batch');
                setBatchStep(1);
                setResult(null);
              }}
              className={`inspector-mode-btn ${mode === 'batch' ? 'inspector-mode-btn--active-batch' : ''}`}
            >
              다중 검사
            </button>
          </div>
        </div>
        <p className="inspector-page-description">
          기재요령 원문 대조 + 맞춤법 검사
        </p>
      </div>

      {/* 비로그인 사용자: 실제 결과 UI 미리보기 (Value First) - 단일 검사 */}
      {!currentUser && !result && mode === 'single' && (
        <div className="inspector-preview-card">
          <p className="inspector-preview-title">AI 검사 결과 미리보기</p>
          <p className="inspector-preview-subtitle">기재요령 원문 대조 + 맞춤법을 한 번에 검사해요</p>
          <div className="inspector-preview-scroll-window" onClick={(e) => { const track = e.currentTarget.querySelector('.inspector-preview-scroll-track'); if (track) { const st = track.style; st.animationPlayState = st.animationPlayState === 'paused' ? 'running' : 'paused'; } }}>
            <div className="inspector-preview-scroll-track">
              {/* 요약 바 */}
              <div className="inspector-summary" style={{ pointerEvents: 'none' }}>
                <div className="inspector-summary-item">
                  <span className="label">개선 권장</span>
                  <span className="value">5개</span>
                </div>
                <div className="inspector-summary-item">
                  <span className="label">나이스 바이트</span>
                  <span className="value">462B</span>
                </div>
              </div>

              {/* 하이라이트된 텍스트 */}
              <div className="inspector-highlighted-section" style={{ pointerEvents: 'none' }}>
                <div className="inspector-result-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h3 style={{ margin: 0, lineHeight: 1 }}>검사 결과</h3>
                    <span className="inspector-model-tag">빠른 모드</span>
                  </div>
                  <span className="inspector-click-hint">색칠된 부분을 클릭하면 상세 정보를 볼 수 있어요</span>
                </div>
                <div className="inspector-highlighted-text">
                  <span>수업 시간에 성실하게 참여</span>
                  <span className="inspector-violation-highlight" style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)', borderBottom: '2px solid #3b82f6' }}>하였으며</span>
                  <span> </span>
                  <span className="inspector-violation-highlight" style={{ backgroundColor: 'rgba(220, 38, 38, 0.12)', borderBottom: '2px solid #dc2626' }}>유튜브</span>
                  <span>를 활용한 탐구 활동에서 주도적인 모습을 보</span>
                  <span className="inspector-violation-highlight" style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)', borderBottom: '2px solid #3b82f6' }}>였음</span>
                  <span>. </span>
                  <span className="inspector-violation-highlight" style={{ backgroundColor: 'rgba(234, 88, 12, 0.2)', borderBottom: '2px solid #ea580c', outline: '2px solid #ea580c', outlineOffset: '1px', borderRadius: '2px' }}>서울대학교 주최 과학탐구대회</span>
                  <span>에서 우수한 성적을 거두</span>
                  <span className="inspector-violation-highlight" style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)', borderBottom: '2px solid #3b82f6' }}>었으며</span>
                  <span>, 또래 친구들과 </span>
                  <span className="inspector-violation-highlight" style={{ backgroundColor: 'rgba(124, 58, 237, 0.12)', borderBottom: '2px solid #7c3aed' }}>비교하여</span>
                  <span> 학습 능력이 뛰어남.</span>
                </div>
              </div>

              {/* 클릭 시 나오는 상세 패널 (인라인) */}
              <div className="inspector-preview-detail-card" style={{ pointerEvents: 'none' }}>
                <div className="inspector-preview-detail-arrow" />
                <h4>개선 권장 상세</h4>
                <div className="inspector-preview-detail-row">
                  <span className="inspector-preview-detail-badge" style={{ backgroundColor: '#ea580c' }}>특정 기관/대회명 기재 금지</span>
                </div>
                <div className="inspector-preview-detail-row">
                  <span className="inspector-preview-detail-label">발견된 표현</span>
                  <span className="inspector-preview-detail-found">"서울대학교 주최 과학탐구대회"</span>
                </div>
                <div className="inspector-preview-detail-row">
                  <span className="inspector-preview-detail-label">개선 이유</span>
                  <span className="inspector-preview-detail-text">특정 대학교명 및 대회명을 기재하면 학생 간 서열화를 유발할 수 있어 기재요령에서 금지하고 있습니다.</span>
                </div>
                <div className="inspector-preview-detail-row">
                  <span className="inspector-preview-detail-label">출처</span>
                  <span className="inspector-preview-detail-text" style={{ fontWeight: 600 }}>2026학년도 학교생활기록부 기재요령 (42페이지)</span>
                </div>
                <div className="inspector-preview-detail-quote">
                  <span className="inspector-preview-detail-label">기재요령 원문</span>
                  <p>"각종 대회에서의 수상실적, 참가 사실, 교외 체험학습 등 교외 활동은 학교생활기록부 어떠한 항목에도 입력하지 않는다."</p>
                </div>
                <div className="inspector-preview-detail-row">
                  <span className="inspector-preview-detail-label">수정 제안</span>
                  <span className="inspector-preview-detail-suggestion">과학탐구 활동</span>
                </div>
              </div>

              {/* AI 수정 제안 */}
              <div className="inspector-suggestion-section" style={{ pointerEvents: 'none' }}>
                <div className="inspector-suggestion-header">
                  <h3>AI 수정 제안</h3>
                  <button className="inspector-copy-button" tabIndex={-1}>복사하기</button>
                </div>
                <div className="inspector-suggestion-text">
                  수업 시간에 성실하게 참여하며 동영상 플랫폼을 활용한 탐구 활동에서 주도적인 모습을 보임. 과학탐구 활동에서 우수한 성적을 거두며, 학습 능력이 뛰어남.
                </div>
                <div className="inspector-suggestion-footer">
                      <span className="inspector-byte-badge">211 바이트</span>
                    </div>
                  </div>
            </div>
            <div className="inspector-preview-fade-top" />
            <div className="inspector-preview-fade-bottom" />
          </div>
          <p className="inspector-preview-stats">
            <CountUp start={0} end={teacherCount} duration={2} separator="," />명의 선생님이 사용 중
          </p>
        </div>
      )}

      {/* 비로그인 사용자: 학급 전체 검사 미리보기 (Value First) */}
      {!currentUser && mode === 'batch' && batchStep === 1 && !batchResults && (
        <div className="inspector-preview-card">
          <p className="inspector-preview-title">학급 전체 검사 미리보기</p>
          <p className="inspector-preview-subtitle">엑셀 한 번 업로드로 학급 전체 생기부를 검사해요</p>
          <div className="inspector-preview-scroll-window" onClick={(e) => { const track = e.currentTarget.querySelector('.inspector-preview-scroll-track'); if (track) { const st = track.style; st.animationPlayState = st.animationPlayState === 'paused' ? 'running' : 'paused'; } }}>
            <div className="inspector-preview-scroll-track">
              {/* 학생 선택 버튼들 */}
              <div style={{ marginBottom: '16px', pointerEvents: 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' }}>학생 선택</h3>
                  <button className="inspector-excel-btn is-preview" tabIndex={-1}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    엑셀 다운로드 (28명)
                  </button>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {['홍길동', '김철수', '이영희', '성춘향', '이몽룡'].map((name, i) => (
                    <button key={i} style={{
                      padding: '8px 16px',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: i === 0 ? '600' : '500',
                      backgroundColor: i === 0 ? '#3182f6' : '#f3f4f6',
                      color: i === 0 ? '#fff' : '#374151',
                      position: 'relative'
                    }} tabIndex={-1}>
                      {name}
                      {i < 3 && (
                        <span style={{
                          position: 'absolute', top: '-4px', right: '-4px',
                          backgroundColor: i === 0 ? '#ef4444' : '#f59e0b',
                          color: '#fff', fontSize: '10px', fontWeight: '700',
                          width: '18px', height: '18px', borderRadius: '50%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>{[3, 5, 1][i]}</span>
                      )}
                    </button>
                  ))}
                  <span style={{ padding: '8px 12px', fontSize: '12px', color: '#9ca3af' }}>+23명</span>
                </div>
              </div>

              {/* 선택된 학생의 검사 결과 */}
              <div className="inspector-summary" style={{ pointerEvents: 'none' }}>
                <div className="inspector-summary-item">
                  <span className="label">개선 권장</span>
                  <span className="value">3개</span>
                </div>
                <div className="inspector-summary-item">
                  <span className="label">나이스 바이트</span>
                  <span className="value">387B</span>
                </div>
              </div>

              <div className="inspector-highlighted-section" style={{ pointerEvents: 'none' }}>
                <div className="inspector-result-header">
                  <h3 style={{ margin: 0, lineHeight: 1 }}>홍길동</h3>
                </div>
                <div className="inspector-highlighted-text">
                  <span>수학적 사고력이 뛰어나 문제 해결 과정에서 창의적인 접근을 시도</span>
                  <span className="inspector-violation-highlight" style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)', borderBottom: '2px solid #3b82f6' }}>하였음</span>
                  <span>. </span>
                  <span className="inspector-violation-highlight" style={{ backgroundColor: 'rgba(220, 38, 38, 0.12)', borderBottom: '2px solid #dc2626', outline: '2px solid #dc2626', outlineOffset: '1px', borderRadius: '2px' }}>칸아카데미</span>
                  <span>를 활용하여 자기주도적으로 학습</span>
                  <span className="inspector-violation-highlight" style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)', borderBottom: '2px solid #3b82f6' }}>하였으며</span>
                  <span> 모둠 활동에서 리더십을 발휘함.</span>
                </div>
              </div>

              {/* 클릭 시 나오는 상세 패널 (인라인) */}
              <div className="inspector-preview-detail-card" style={{ pointerEvents: 'none', borderLeftColor: '#dc2626' }}>
                <div className="inspector-preview-detail-arrow" />
                <h4>개선 권장 상세</h4>
                <div className="inspector-preview-detail-row">
                  <span className="inspector-preview-detail-badge" style={{ backgroundColor: '#dc2626' }}>브랜드명/상표명 기재 금지</span>
                </div>
                <div className="inspector-preview-detail-row">
                  <span className="inspector-preview-detail-label">발견된 표현</span>
                  <span className="inspector-preview-detail-found">"칸아카데미"</span>
                </div>
                <div className="inspector-preview-detail-row">
                  <span className="inspector-preview-detail-label">개선 이유</span>
                  <span className="inspector-preview-detail-text">특정 브랜드명이나 상표명은 학교생활기록부에 기재할 수 없습니다. 일반 명칭으로 대체해야 합니다.</span>
                </div>
                <div className="inspector-preview-detail-quote">
                  <span className="inspector-preview-detail-label">기재요령 원문</span>
                  <p>"학교생활기록부에는 상품명이나 브랜드명 등의 특정 업체와 관련된 내용을 기재하지 않는다."</p>
                </div>
                <div className="inspector-preview-detail-row">
                  <span className="inspector-preview-detail-label">수정 제안</span>
                  <span className="inspector-preview-detail-suggestion">온라인 학습 플랫폼</span>
                </div>
              </div>

              <div className="inspector-suggestion-section" style={{ pointerEvents: 'none' }}>
                <div className="inspector-suggestion-header">
                  <h3>AI 수정 제안</h3>
                  <button className="inspector-copy-button" tabIndex={-1}>복사하기</button>
                </div>
                <div className="inspector-suggestion-text">
                  수학적 사고력이 뛰어나 문제 해결 과정에서 창의적인 접근을 시도함. 온라인 학습 플랫폼을 활용하여 자기주도적으로 학습하며 모둠 활동에서 리더십을 발휘함.
                </div>
                <div className="inspector-suggestion-footer">
                  <span className="inspector-byte-badge">215 바이트</span>
                </div>
              </div>
            </div>
            <div className="inspector-preview-fade-top" />
            <div className="inspector-preview-fade-bottom" />
          </div>
          <p className="inspector-preview-stats">
            <CountUp start={0} end={teacherCount} duration={2} separator="," />명의 선생님이 사용 중
          </p>
        </div>
      )}

      {/* 단일 검사 UI (단일 검사 + 일괄 결과 공유) */}
      {(mode === 'single' || (mode === 'batch' && batchStep === 3)) && (
      <div className={`inspector-content ${resultHasViolations ? 'has-result' : ''}`}>
        {/* 왼쪽: 입력 및 결과 */}
        <div className="inspector-left">
          {/* 학교급 및 검사 기준 선택 (단일 검사에서만) */}
          {mode === 'single' && (
          <div className="inspector-school-selector">
            <label>학교급</label>
            <div className="inspector-school-buttons">
              <button
                className={schoolLevel === 'elementary' ? 'active' : ''}
                onClick={() => handleSchoolLevelChange('elementary')}
              >
                초등학교
              </button>
              <button
                className={schoolLevel === 'middle' ? 'active' : ''}
                onClick={() => handleSchoolLevelChange('middle')}
              >
                중학교
              </button>
              <button
                className={schoolLevel === 'high' ? 'active' : ''}
                onClick={() => handleSchoolLevelChange('high')}
              >
                고등학교
              </button>
            </div>
            <InspectorGuidelineContextSelector
              academicYear={academicYear}
              disabled={loading}
              idPrefix="inspector-single"
              onAcademicYearChange={handleAcademicYearChange}
              onRecordGradeChange={handleRecordGradeChange}
              recordGrade={recordGrade}
              schoolLevel={schoolLevel}
              showGradeError={showGuidelineContextError}
            />
          </div>
          )}
          
          {/* 학생 선택 (일괄 검사 Step3에서만) */}
          {mode === 'batch' && batchStep === 3 && batchResults && (
            <div style={{ marginBottom: '24px' }}>
              <InspectorClassCheck
                plan={inspectorOverlapPlan}
                names={batchResults.results.map((student) => student.student_name)}
                selectedIndex={selectedBatchStudent}
                approximate={batchApproximate}
                onShown={handleOverlapShown}
                onSelect={(index) => {
                  if (refineLoading || savingRefine) return;
                  const student = batchResults.results[index];
                  if (!student) return;
                  setSelectedBatchStudent(index);
                  loadStudentResult(student, index, batchResults?.record_id);
                  trackEvent('inspector_class_check_open_student', {
                    record_id: batchResults?.record_id || null,
                    mode: 'batch',
                    student_index: index,
                    school_level: schoolLevel,
                  });
                }}
              />
              {/* 상단: 제목과 엑셀 다운로드 버튼 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' }}>학생 선택</h3>
                <button
                  className="inspector-excel-btn"
                  onClick={handleExcelDownload}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  검사 결과 엑셀 다운로드 ({batchResults.results.length}명)
                </button>
              </div>
              
              {/* 학생 버튼들 */}
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {batchResults.results.map((student, idx) => {
                  const violations = (student.basic_violations?.length || 0) + (student.guideline_violations?.length || 0);
                  const isSelected = selectedBatchStudent === idx;
                  const hasError = Boolean(student.error);
                  
                  return (
                    <button
                      key={idx}
                      disabled={refineLoading || savingRefine}
                      onClick={() => {
                        setSelectedBatchStudent(idx);
                        loadStudentResult(student, idx, batchResults?.record_id);
                      }}
                      className={`inspector-student-chip${isSelected ? ' is-selected' : ''}${hasError ? ' has-error' : ''}`}
                    >
                      {idx + 1}. {student.student_name}
                      {overlapTargetByIndex.has(idx) && (
                        <span className="inspector-student-chip-overlap" title="다른 학생과 겹치는 문장이 있어요" />
                      )}
                      {hasError ? (
                        <span className="inspector-student-chip-badge is-failed">
                          실패
                        </span>
                      ) : violations > 0 && (
                        <span className="inspector-student-chip-badge">
                          {violations}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {overlapTargetByIndex.has(selectedBatchStudent) && (
                <div style={{ marginTop: '16px' }}>
                  <InspectorOverlapSentences target={overlapTargetByIndex.get(selectedBatchStudent)} />
                </div>
              )}
            </div>
          )}
          
          {/* 입력 영역 (단일 검사 모드에서만) */}
          {mode === 'single' && !loading && (
          <>
          <div className="inspector-input-section">
            <div className="inspector-input-header">
              <label>검사할 문장</label>
              <span className="inspector-byte-count">
                {calculateNeisBytes(inputText)} 바이트
              </span>
            </div>
            <button
              className="inspector-example-button"
              onClick={() => replaceInspectorDraft('수업시간에  성실하게 참여하였으며 서울대학교 진학을 목표로 다양한 활동에 임했다. 영어 듣기 능력이 뛰어나 유창하게말하며 과제를 함께 해결했다. 학교 체육 활동에서 나이키 운동화를 신고적극적으로 참여하였고, 열심히 노력하여 좋은 성과를 거두었다. 매우 매우 성실하며 친구들과 협력을  잘함. 어려운 과제도 끝까지 완수하고싶어졌으며, 학셍의 학습 방법을 스스로 개선하려는 태도를 보였음.')}
              type="button"
            >
              예시로 체험하기
            </button>
            <textarea
              className="inspector-textarea"
              value={inputText}
              onChange={(e) => replaceInspectorDraft(e.target.value)}
              placeholder="생기부 문장을 붙여넣으세요"
              rows={5}
            />
          </div>
          
          {/* AI 모델 선택 (비로그인 시 숨김 - 기본값으로 검사) */}
          {!isAnonymousUser && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{
              display: 'flex',
              gap: '8px',
              padding: '4px',
              backgroundColor: '#f3f4f6',
              borderRadius: '10px',
              width: 'fit-content'
            }}>
              <button
                onClick={() => setSelectedModel('gemini-2.5-flash-lite')}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: selectedModel === 'gemini-2.5-flash-lite' ? '#fff' : 'transparent',
                  color: selectedModel === 'gemini-2.5-flash-lite' ? '#191f28' : '#6b7280',
                  fontSize: '13px',
                  fontWeight: selectedModel === 'gemini-2.5-flash-lite' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  boxShadow: selectedModel === 'gemini-2.5-flash-lite' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                }}
              >
                빠른 모드
              </button>
              <button
                onClick={() => setSelectedModel('gemini-2.5-flash')}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: selectedModel === 'gemini-2.5-flash' ? '#fff' : 'transparent',
                  color: selectedModel === 'gemini-2.5-flash' ? '#191f28' : '#6b7280',
                  fontSize: '13px',
                  fontWeight: selectedModel === 'gemini-2.5-flash' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  boxShadow: selectedModel === 'gemini-2.5-flash' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                고급 모드
                <span style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: selectedModel === 'gemini-2.5-flash' ? '#3182f6' : '#9ca3af',
                  padding: '1px 4px',
                  borderRadius: '3px',
                  backgroundColor: selectedModel === 'gemini-2.5-flash' ? '#eff6ff' : 'transparent'
                }}>
                  PRO
                </span>
              </button>
            </div>
            <p style={{ 
              fontSize: '12px', 
              color: '#9ca3af', 
              marginTop: '6px',
              marginBottom: 0,
              marginLeft: '4px'
            }}>
              {selectedModel === 'gemini-2.5-flash-lite' ? '빠르게 검사' : '더 정확하고 상세한 검사'}
            </p>
          </div>
          )}

          {/* 검사 버튼 (단일 검사 모드에서만) */}
          <button
            className="inspector-check-button"
            onClick={handleCheck}
            disabled={loading || !inputText.trim()}
          >
            {loading ? '검사 중...' : '검사하기'}
          </button>
          </>
          )}
          
          {/* 🆕 검사 중 로딩 화면 */}
          {mode === 'single' && loading && (
            <TossLoadingSpinner 
              message="기재요령 원문과 대조하고 맞춤법을 검사하고 있어요"
              type="inspector"
              showProgress={true}
              showTips={true}
              scrollToModeButtons={scrollToModeButtons}
            />
          )}
          
          {/* 결과 표시 */}
          {result && (
            <div className="inspector-result-section" ref={resultSectionRef}>
              {/* 요약 정보 */}
              <div className="inspector-summary">
                <div className="inspector-summary-item">
                  <span className="label">개선 권장</span>
                  <span className="value">
                    {(result.basic_violations?.length || 0) + (result.guideline_violations?.length || 0)}개
                  </span>
                </div>
                <div className="inspector-summary-item">
                  <span className="label">나이스 바이트</span>
                  <span className="value">{result.bytes}B</span>
                </div>
              </div>
              
              {/* 하이라이트된 텍스트 */}
              <div className="inspector-highlighted-section">
                <div className="inspector-result-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h3 style={{ margin: 0, lineHeight: 1 }}>검사 결과</h3>
                    {/* 🆕 모델 태그 */}
                    {selectedModel === 'gemini-2.5-flash' ? (
                      <span className="inspector-model-tag is-advanced">
                        고급 모드
                      </span>
                    ) : (
                      <span className="inspector-model-tag">
                        빠른 모드
                      </span>
                    )}
                    <span className="inspector-context-badge">
                      {getInspectorGuidelineContextLabel(
                        result.academic_year || academicYear,
                        result.record_grade || recordGrade,
                      )}
                    </span>
                    {selectedModel === 'gemini-2.5-flash-lite' && !isProUser && (
                      <button
                        className="inspector-upgrade-link"
                        onClick={() => requireProAccess('inspector')}
                      >
                        고급 모드 체험 →
                      </button>
                    )}
                  </div>
                  {((result.basic_violations?.length || 0) + (result.guideline_violations?.length || 0) > 0) && (
                    <span className="inspector-click-hint">
                      색칠된 부분을 클릭하면 상세 정보를 볼 수 있어요
                    </span>
                  )}
                </div>
                {renderHighlightedText()}
                {result.error ? (
                  <div className="inspector-no-violations" style={{
                    backgroundColor: '#fef2f2',
                    borderColor: '#fecaca',
                    color: '#991b1b'
                  }}>
                    {result.error_message || '검사 결과를 해석하지 못했어요. 이 학생만 다시 검사해주세요.'}
                  </div>
                ) : (result.basic_violations?.length || 0) + (result.guideline_violations?.length || 0) === 0 && (
                  <div className="inspector-no-violations">
                    개선 권장사항 없음 (기재요령 준수)
                  </div>
                )}
              </div>
              
              {renderInspectorSuggestion()}

              {/* 비로그인 사용자: 저장 + 연속 검사 복합 CTA */}
              {result && isAnonymousUser && (
                <div style={{
                  marginTop: '16px',
                  padding: '20px',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #EFF6FF 0%, #F0F9FF 100%)',
                  border: '1px solid #DBEAFE'
                }}>
                  <p style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#1e40af',
                    margin: '0 0 6px 0'
                  }}>
                    검사 기록이 저장되지 않아요
                  </p>
                  <p style={{
                    fontSize: '13px',
                    color: '#6b7280',
                    margin: '0 0 16px 0',
                    lineHeight: '1.5'
                  }}>
                    무료 가입하면 검사 기록을 저장하고 이어서 검사할 수 있어요
                  </p>
                  <button
                    onClick={() => {
                      setLoginPromptFeature('inspector');
                      setShowLoginPrompt(true);
                    }}
                    className="inspector-signup-btn"
                  >
                    무료로 시작하기
                  </button>
                </div>
              )}

              {/* 로그인 사용자: 하단 액션 링크 */}
              {result && !isAnonymousUser && mode === 'single' && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '16px',
                  marginTop: '12px'
                }}>
                  <button
                    className="inspector-text-link"
                    onClick={() => navigate('/inspector-history')}
                  >
                    내 검사 기록
                  </button>
                  <span style={{
                    width: '1px',
                    height: '14px',
                    background: '#e5e7eb'
                  }} />
                  <button
                    onClick={() => {
                      setMode('batch');
                      setBatchStep(1);
                      setResult(null);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="inspector-text-link"
                  >
                    30명 한번에 검사하기
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* 오른쪽: 위반이 있는 결과가 생긴 순간부터 자리를 유지해 상세 선택 시 레이아웃 이동 방지 */}
        {resultHasViolations && (
          <div className={`inspector-right-panel ${selectedViolation ? '' : 'is-empty'}`} ref={detailPanelRef}>
            {selectedViolation ? (
              <div className="inspector-detail-content">
                {selectedViolation.isGroup ? (
                // 다중 위반사항 표시
                <>
                  <h3>개선 권장 상세 ({selectedViolation.count}개)</h3>
                  
                  <div className="inspector-detail-section">
                    <div className="inspector-detail-label">발견된 표현</div>
                    <div className="inspector-detail-value found-text">
                      {`"${/^\s+$/.test(selectedViolation.found)
                        ? selectedViolation.found.replace(/ /g, '□').replace(/\n/g, '↵')
                        : selectedViolation.found}"`}
                    </div>
                  </div>
                  
                  {/* 모든 위반사항 리스트 */}
                  <div className="inspector-violations-list">
                    {selectedViolation.violations.map((violation, index) => {
                      // category 기반 색상 (없으면 severity 기반 fallback)
                      const violationColor = violation.category && CATEGORY_COLORS[violation.category]
                        ? CATEGORY_COLORS[violation.category]
                        : SEVERITY_COLORS[VIOLATION_SEVERITY[violation.type] || 0] || '#6b7280';
                      
                      return (
                        <div key={index} className="inspector-violation-item">
                          <div className="inspector-violation-header">
                            <span 
                              className="inspector-violation-type-badge"
                              style={{ 
                                backgroundColor: violationColor,
                                color: '#fff'
                              }}
                            >
                              {violation.type}
                            </span>
                          </div>
                          
                          {/* 개선 이유/참고 사항 */}
                          {(violation.reason || violation.rule) && (
                            <div className="inspector-violation-reason">
                              <strong>{violation.reason ? '개선 이유' : '참고 사항'}:</strong>
                              <p>{violation.reason || violation.rule}</p>
                            </div>
                          )}
                          
                          {/* 출처 정보 (guideline_violations만) */}
                          {violation.file && (
                            <div className="inspector-violation-source">
                              <strong>출처:</strong> {violation.file}
                              {guidelinePageLabel(violation.page)}
                            </div>
                          )}
                          
                          {/* 기재요령 원문 */}
                          {violation.quote && (
                            <div className="inspector-violation-quote">
                              <strong>기재요령 원문:</strong>
                              <p>"{violation.quote}"</p>
                            </div>
                          )}
                          
                          {/* 수정 제안 */}
                          {violation.suggestion !== undefined && (
                            <div className="inspector-violation-suggestion">
                              <strong>수정 제안:</strong>
                              <p className="suggestion-text">
                                {violation.suggestion === '' 
                                  ? '(삭제)' 
                                  : /^\s+$/.test(violation.suggestion)
                                    ? violation.suggestion.replace(/ /g, '□').replace(/\n/g, '↵')
                                    : violation.suggestion}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                // 단일 위반사항 표시 (기존 로직)
                <>
                  <h3>개선 권장 상세</h3>
                  
                  <div className="inspector-detail-section">
                    <div className="inspector-detail-label">유형</div>
                    <div 
                      className="inspector-detail-value violation-type"
                      style={{ 
                        backgroundColor: selectedViolation.category && CATEGORY_COLORS[selectedViolation.category]
                          ? CATEGORY_COLORS[selectedViolation.category]
                          : SEVERITY_COLORS[VIOLATION_SEVERITY[selectedViolation.type] || 0] || '#6b7280',
                        color: '#fff',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        fontWeight: 'bold'
                      }}
                    >
                      {selectedViolation.type}
                    </div>
                  </div>
                  
                  <div className="inspector-detail-section">
                    <div className="inspector-detail-label">발견된 표현</div>
                    <div className="inspector-detail-value found-text">
                      "{/^\s+$/.test(selectedViolation.found) 
                        ? selectedViolation.found.replace(/ /g, '□').replace(/\n/g, '↵') 
                        : selectedViolation.found}"
                    </div>
                  </div>
                  
                  {/* 개선 이유 */}
                  {selectedViolation.reason && (
                    <div className="inspector-detail-section">
                      <div className="inspector-detail-label">
                        개선 이유
                      </div>
                      <div className="inspector-detail-value reason-text">
                        {selectedViolation.reason}
                      </div>
                    </div>
                  )}
                  
                  {/* 참고 사항 */}
                  {selectedViolation.rule && (
                    <div className="inspector-detail-section">
                      <div className="inspector-detail-label">
                        참고 사항
                      </div>
                      <div className="inspector-detail-value reason-text">
                        {selectedViolation.rule}
                      </div>
                    </div>
                  )}
                  
                  {/* 기재요령 출처 정보 */}
                  {selectedViolation.file && (
                    <div className="inspector-detail-section">
                      <div className="inspector-detail-label">
                        출처 파일
                      </div>
                      <div className="inspector-detail-value file-text">
                        {selectedViolation.file}
                        {guidelinePageLabel(selectedViolation.page)}
                      </div>
                    </div>
                  )}
                  
                  {/* 기재요령 원문 인용 */}
                  {selectedViolation.quote && (
                    <div className="inspector-detail-section">
                      <div className="inspector-detail-label">
                        기재요령 원문
                      </div>
                      <div className="inspector-detail-value quote-text">
                        "{selectedViolation.quote}"
                      </div>
                    </div>
                  )}
                  
                  {selectedViolation.suggestion !== undefined && (
                    <div className="inspector-detail-section">
                      <div className="inspector-detail-label">
                        수정 제안
                      </div>
                      <div className="inspector-detail-value suggestion-text">
                        {selectedViolation.suggestion === '' 
                          ? '(삭제)' 
                          : /^\s+$/.test(selectedViolation.suggestion)
                            ? selectedViolation.suggestion.replace(/ /g, '□').replace(/\n/g, '↵')
                            : selectedViolation.suggestion}
                      </div>
                    </div>
                  )}
                </>
                )}
              </div>
            ) : (
              <div className="inspector-detail-empty">
                <div className="inspector-detail-empty-icon" aria-hidden="true">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                    <path d="M8 11h6M11 8v6" />
                  </svg>
                </div>
                <h3>강조된 표현을 선택해주세요</h3>
                <p>선택한 표현의 개선 이유와 수정 제안을 여기에서 확인할 수 있어요.</p>
              </div>
            )}
          </div>
        )}
        
        {/* 새로운 검사 시작 버튼 (일괄 검사 결과에서만, 하단에 표시) */}
        {mode === 'batch' && batchStep === 3 && batchResults && (
          <div style={{ textAlign: 'center', marginTop: '40px', marginBottom: '20px' }}>
            <button
              onClick={() => {
                setBatchStep(1);
                setBatchResults(null);
                // setBatchStudents([]); // 사용하지 않음 - 제거
                setResult(null);
                setInputText('');
                setSelectedViolation(null);
                setSelectedBatchStudent(0);
              }}
              style={{
                padding: '12px 24px',
                backgroundColor: '#3182f6',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              새로운 검사 시작
            </button>
          </div>
        )}
      </div>
      )}

      {/* 엑셀 일괄 검사 모드 */}
      {mode === 'batch' && (
        <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
          {/* Step 1: 엑셀 업로드 - 깔끔한 디자인 */}
          {batchStep === 1 && (
            <div className="saenggibu-batch-excel-upload">
              {/* 학교급 선택 - 기존 사용자에게 익숙한 카드 스타일 */}
              <div style={{
                backgroundColor: '#fff',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '20px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', whiteSpace: 'nowrap' }}>학교급</span>
                  <div style={{ display: 'flex', gap: '6px', flex: 1 }}>
                    {[
                      { value: 'elementary', label: '초등' },
                      { value: 'middle', label: '중등' },
                      { value: 'high', label: '고등' }
                    ].map((level) => (
                      <button
                        key={level.value}
                        onClick={() => handleSchoolLevelChange(level.value)}
                        disabled={batchLoading}
                        style={{
                          flex: 1,
                          padding: '10px 16px',
                          fontSize: '13px',
                          fontWeight: schoolLevel === level.value ? '600' : '500',
                          color: schoolLevel === level.value ? '#fff' : '#6b7280',
                          background: schoolLevel === level.value ? '#3182f6' : '#f3f4f6',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: batchLoading ? 'not-allowed' : 'pointer',
                          opacity: batchLoading ? 0.6 : 1,
                          transition: 'all 0.15s ease'
                        }}
                      >
                        {level.label}
                      </button>
                    ))}
                  </div>
                </div>
                <InspectorGuidelineContextSelector
                  academicYear={academicYear}
                  disabled={batchLoading || batchPasteLoading}
                  idPrefix="inspector-batch"
                  onAcademicYearChange={handleAcademicYearChange}
                  onRecordGradeChange={handleRecordGradeChange}
                  recordGrade={recordGrade}
                  schoolLevel={schoolLevel}
                  showGradeError={showGuidelineContextError}
                />
              </div>

              {/* 파일 업로드 영역 - 깔끔한 디자인 */}
              <div
                className={`saenggibu-batch-excel-drop-zone ${isDragging ? 'dragging' : ''} ${batchFile ? 'uploaded' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={!batchFile && !batchLoading ? async (e) => {
                  // 파일 input 자신의 click이 버블링되어 재진입하면 무한 파일선택 루프
                  // (콘솔 경고 폭주 → 탭 먹통)가 되므로 반드시 차단한다.
                  if (e.target?.closest?.('input[type="file"]')) return;
                  if (isProUser) {
                    // 파일 선택창은 사용자 제스처(동기 콜스택) 안에서만 열 수 있다.
                    document.getElementById('batch-excel-input').click();
                    return;
                  }
                  if (await requireProAccess('inspector-batch')) {
                    document.getElementById('batch-excel-input').click();
                  }
                } : undefined}
                style={{ 
                  minHeight: batchFile ? '80px' : '160px',
                  padding: batchFile ? '16px 20px' : '32px 24px',
                  marginBottom: '20px',
                  cursor: !batchFile && !batchLoading ? 'pointer' : 'default'
                }}
              >
                <input
                  type="file"
                  accept={SUPPORTED_UPLOAD_ACCEPT}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      performFilePreCheck(e.target.files[0]);
                    }
                  }}
                  style={{ display: 'none' }}
                  id="batch-excel-input"
                />

                {!batchFile ? (
                  <div className="saenggibu-batch-drop-zone-content" style={{ gap: '12px' }}>
                    <div style={{ 
                      width: '48px', 
                      height: '48px', 
                      borderRadius: '12px', 
                      backgroundColor: '#eff6ff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                    </div>
                    
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        fontSize: '15px', 
                        margin: '0 0 10px 0', 
                        fontWeight: '600', 
                        color: '#111827'
                      }}>
                        파일을 그대로 올리세요
                        <FieldHelpTooltip
                          title={INSPECTOR_BATCH_HELP.upload.title}
                          items={INSPECTOR_BATCH_HELP.upload.items}
                          ariaLabel="다중 검사 파일 안내"
                          variant="compact"
                        />
                      </p>
                      <p style={{ 
                        fontSize: '12px', 
                        margin: '0', 
                        color: '#9ca3af' 
                      }}>
                        Excel, PDF, PPT, 한글, 이미지 • 최대 {Math.round(getFileLimits(isProUser).fileSize / (1024 * 1024))}MB
                      </p>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ 
                      width: '36px', 
                      height: '36px', 
                      borderRadius: '10px', 
                      backgroundColor: '#ecfdf5', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5">
                        <polyline points="20,6 9,17 4,12"/>
                      </svg>
                    </div>
                    
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {batchFile.name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        {(batchFile.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setBatchFile(null);
                        setUploadError(null);
                      }}
                      style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#6b7280',
                        backgroundColor: '#f3f4f6',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      변경
                    </button>
                  </div>
                )}

              </div>

              {/* 업로드 진행률 */}
              {batchLoading && (
                <div style={{
                  backgroundColor: '#f3f4f6',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '24px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                      AI가 학생 정보를 분석하고 있어요
                    </span>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#3182f6' }}>
                      {uploadProgress}%
                    </span>
                  </div>
                  <div style={{ 
                    height: '6px', 
                    backgroundColor: '#e5e7eb', 
                    borderRadius: '3px', 
                    overflow: 'hidden' 
                  }}>
                    <div 
                      style={{ 
                        width: `${uploadProgress}%`,
                        height: '100%',
                        backgroundColor: '#3182f6',
                        transition: 'width 0.3s ease',
                        borderRadius: '3px'
                      }}
                    />
                  </div>
                  <p style={{ fontSize: '13px', color: '#9ca3af', margin: '8px 0 0 0', textAlign: 'center' }}>
                    {uploadRemainingTime > 0 
                      ? `약 ${uploadRemainingTime}초 남음` 
                      : '거의 완료됐어요'}
                  </p>
                </div>
              )}

              {/* 안내 문구 */}
              {!batchFile && !batchLoading && (
                <div className="inspector-batch-assist">
                  <details ref={inspectorAssistDetailsRef}>
                    <summary>처음이라 막막하신가요?</summary>
                    <div className="inspector-batch-assist-card">
                      <button type="button" className="assist-sheet-close" onClick={closeInspectorAssistGuide} aria-label="도움말 닫기">닫기</button>
                      <strong>갖고 있는 파일을 그대로 올려주세요</strong>
                      <span>나이스에서 내려받은 파일, 엑셀, 한글, PDF, 이미지 모두 괜찮아요. 학생명과 생기부 내용이 보이면 AI가 최대한 찾아서 검사해요.</span>
                      <div className="inspector-batch-assist-actions">
                        <button type="button" onClick={() => {
                          closeInspectorAssistGuide();
                          setShowInspectorTemplateGuide(true);
                        }}>
                          빈 양식 받기
                        </button>
                        <button type="button" onClick={handleFillInspectorExample}>
                          예시 보기
                        </button>
                      </div>
                    </div>
                  </details>
                </div>
              )}

              {!batchFile && !batchLoading && (
                <div className="saenggibu-batch-paste-panel inspector-batch-paste-panel">
                  <div className="saenggibu-batch-paste-guide">
                    <strong>
                      엑셀 표 붙여넣기
                      <FieldHelpTooltip
                        title={INSPECTOR_BATCH_HELP.paste.title}
                        items={INSPECTOR_BATCH_HELP.paste.items}
                        ariaLabel="다중 검사 엑셀 붙여넣기 안내"
                        variant="compact"
                      />
                    </strong>
                  </div>
                  <div className="saenggibu-batch-sheet-editor" onPaste={handleInspectorGridPaste}>
                    <div className="saenggibu-batch-sheet-row header">
                      <span>번호</span>
                      <span>학생명</span>
                      <span>생기부 내용</span>
                    </div>
                    {batchPasteRows.map((row, index) => (
                      <div className="saenggibu-batch-sheet-row editable" key={`inspector-paste-row-${index}`}>
                        <input
                          value={row.no}
                          onChange={(e) => handleInspectorCellChange(index, 'no', e.target.value)}
                          aria-label={`${index + 1}번 행 번호`}
                          disabled={batchPasteLoading || batchLoading}
                        />
                        <input
                          value={row.name}
                          onChange={(e) => handleInspectorCellChange(index, 'name', e.target.value)}
                          placeholder={index === 0 ? '김왓퀴' : ''}
                          aria-label={`${index + 1}번 행 학생명`}
                          disabled={batchPasteLoading || batchLoading}
                        />
                        <textarea
                          value={row.content}
                          onChange={(e) => handleInspectorCellChange(index, 'content', e.target.value)}
                          placeholder={index === 0 ? '엑셀에서 복사한 생기부 내용을 표 안에 그대로 붙여넣으세요' : ''}
                          aria-label={`${index + 1}번 행 생기부 내용`}
                          disabled={batchPasteLoading || batchLoading}
                          rows={1}
                        />
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="saenggibu-batch-add-row-btn"
                    onClick={handleAddInspectorPasteRow}
                    disabled={batchPasteLoading || batchLoading}
                  >
                    행 추가하기
                  </button>
                  {batchPasteError && (
                    <div className="saenggibu-batch-paste-error">
                      {batchPasteError}
                    </div>
                  )}
                  <div className="saenggibu-batch-paste-actions">
                    <button type="button" className="secondary" onClick={handleFillInspectorExample} disabled={batchPasteLoading || batchLoading}>
                      예시 채우기
                    </button>
                    <button type="button" className="primary" onClick={handleAnalyzeInspectorPaste} disabled={batchPasteLoading || batchLoading}>
                      {batchPasteLoading || batchLoading ? '검사 준비 중...' : '붙여넣은 내용 검사하기'}
                    </button>
                  </div>
                  <p className="saenggibu-batch-paste-note">
                    학생별 내용을 기재요령 원문과 맞춤법 기준으로 검사해요.
                  </p>
                </div>
              )}

              {/* 에러/경고 메시지 표시 */}
              {uploadError && (
                <div style={{
                  marginTop: '16px',
                  padding: '16px 20px',
                  backgroundColor: uploadError.type === 'warning' ? '#fefce8' : '#fef2f2',
                  borderRadius: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{ flexShrink: 0, marginTop: '2px' }}>
                      {uploadError.type === 'warning' ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#a16207" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                        </svg>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ 
                        margin: 0, 
                        fontSize: '15px',
                        fontWeight: '700', 
                        color: uploadError.type === 'warning' ? '#a16207' : '#991b1b',
                        marginBottom: '4px'
                      }}>
                        {uploadError.message}
                      </h4>
                      <p style={{ 
                        margin: 0, 
                        color: uploadError.type === 'warning' ? '#ca8a04' : '#b91c1c', 
                        fontSize: '14px',
                        lineHeight: '1.6',
                        marginBottom: '12px'
                      }}>
                        {uploadError.detail}
                      </p>
                      
                      {/* 에러 타입별 해결 방법 안내 */}
                      {uploadError.type === 'invalid_format' && (
                        <div style={{
                          padding: '10px 12px',
                          backgroundColor: 'rgba(255, 255, 255, 0.6)',
                          borderRadius: '8px',
                          fontSize: '13px',
                          color: '#6b7280'
                        }}>
                          <strong>해결 방법:</strong><br/>
                          • Excel, PDF, PPT, 한글, 이미지 파일인지 확인해주세요<br/>
                          • 지원 형식: .xlsx, .xls, .pdf, .ppt, .pptx, .hwp, .hwpx, .jpg, .png
                        </div>
                      )}
                      
                      {uploadError.type === 'no_students_found' && (
                        <div style={{
                          padding: '10px 12px',
                          backgroundColor: 'rgba(255, 255, 255, 0.6)',
                          borderRadius: '8px',
                          fontSize: '13px',
                          color: '#6b7280'
                        }}>
                          <strong>해결 방법:</strong><br/>
                          • 학생명(이름/학생명/성명)이 파일 안에 보이는지 확인해주세요<br/>
                          • 생기부 내용(생기부/행발/세특/종합의견)이 함께 들어있는지 확인해주세요<br/>
                          • 완성된 문장(~함, ~임 등으로 끝나는)이 포함되어 있는지 확인해주세요<br/>
                          • 꼭 정해진 양식일 필요는 없지만, 이름과 내용이 모두 있어야 해요<br/>
                          <br/>
                          <strong>올바른 예시:</strong><br/>
                          <div style={{
                            padding: '8px',
                            backgroundColor: '#f0fdf4',
                            borderRadius: '4px',
                            marginTop: '6px',
                            fontSize: '12px',
                            fontFamily: 'monospace'
                          }}>
                            | 학생명 | 생기부 내용 |<br/>
                            | 김철수 | 수업에 적극적으로 참여하며... |<br/>
                            | 이영희 | 모둠 활동에서 리더십을... |
                          </div>
                        </div>
                      )}
                      
                      {uploadError.type === 'invalid_data' && uploadError.type !== 'no_students_found' && (
                        <div style={{
                          padding: '10px 12px',
                          backgroundColor: 'rgba(255, 255, 255, 0.6)',
                          borderRadius: '8px',
                          fontSize: '13px',
                          color: '#6b7280'
                        }}>
                          <strong>해결 방법:</strong><br/>
                          • 학생명과 생기부 내용이 파일 안에 함께 있는지 확인해주세요<br/>
                          • 빈 행이나 불필요한 데이터가 많다면 줄여서 다시 올려주세요<br/>
                          • 정해진 양식이 아니어도 괜찮아요
                        </div>
                      )}
                      
                      {uploadError.type === 'server_error' && (
                        <div style={{
                          padding: '10px 12px',
                          backgroundColor: 'rgba(255, 255, 255, 0.6)',
                          borderRadius: '8px',
                          fontSize: '13px',
                          color: '#6b7280'
                        }}>
                          <strong>해결 방법:</strong><br/>
                          • 잠시 후 다시 시도해주세요<br/>
                          • 문제가 계속되면 고객지원에 문의해주세요
                        </div>
                      )}
                      
                      <button
                        className="inspector-error-close-btn"
                        onClick={() => setUploadError(null)}
                      >
                        닫기
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Step 2: 검사 중 - 프로그레스바 */}
          {batchStep === 2 && (
            <div className="saenggibu-batch-processing">
              <div className="saenggibu-batch-step-header">
                <h2>검사 진행 중...</h2>
                <p className="saenggibu-batch-step-description">
                  기재요령 위반 · 맞춤법 · 금지표현 검사 중
                </p>
              </div>

              <div className="saenggibu-batch-processing-content">
                <div className="saenggibu-batch-progress-section">
                  <div className="saenggibu-batch-progress-info">
                    <span className="saenggibu-batch-progress-label">검사 중</span>
                    <span className="saenggibu-batch-progress-percentage">
                      {batchCheckCounts
                        ? `${batchCheckCounts.completed}/${batchCheckCounts.total}명 완료`
                        : (currentChecking.match(/^\d+명/)?.[0] || 'AI 분석')}
                    </span>
                  </div>
                  <div className="saenggibu-batch-progress-bar">
                    <div 
                      className="saenggibu-batch-progress-fill" 
                      style={{ width: `${batchProgress}%` }}
                    />
                  </div>
                  <p className="saenggibu-batch-progress-note">{currentChecking}</p>
                </div>
                <BatchLoadingMiniGame label="검사 기다리는 동안 점프!" />
                
                <div style={{
                  marginTop: '24px',
                  padding: '16px 20px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '12px'
                }}>
                  <p style={{ 
                    margin: 0, 
                    color: '#6b7280', 
                    fontSize: '14px',
                    lineHeight: '1.6'
                  }}>
                    각 학생의 생기부를 <strong style={{ color: '#374151' }}>기재요령 원문과 대조</strong>해서 검사해요. 위반사항과 AI 수정 제안을 자동으로 알려줘요.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Step 3: 위의 학생 선택 탭 + 단일 검사 UI가 자동으로 표시됨 */}
        </div>
      )}

      {/* 로그인 프롬프트 모달 */}
      <LoginPrompt
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        feature={loginPromptFeature}
      />
      
      {/* PRO 업그레이드 모달 */}
      {showProModal && (
        <ProUpgradeModal
          isOpen={showProModal}
          onClose={() => setShowProModal(false)}
          feature="학급 전체 검사"
          description="학급 전체 학생의 생기부를 한 번에 검사하고 AI 수정 제안을 받으세요. PRO에서 무제한으로 사용할 수 있어요."
          trigger="not_pro"
        />
      )}

      {/* 참고용 양식 안내 — 양식 없이도 내 파일을 그대로 올릴 수 있다는 점을 먼저 알린다 */}
      <Dialog
        description="이미 갖고 있는 나이스 파일, 엑셀, 한글, PDF가 있다면 그대로 올려주세요. 빈 양식은 처음 시작할 때 참고용으로만 쓰면 돼요."
        footer={(
          <ModalActions
            primary={{ label: '참고용 양식 받기', onClick: handleConfirmInspectorTemplateDownload }}
            secondary={{ label: '내 파일 그대로 올릴게요', onClick: () => setShowInspectorTemplateGuide(false) }}
            testId="inspector-template-guide-actions"
          />
        )}
        onClose={() => setShowInspectorTemplateGuide(false)}
        open={showInspectorTemplateGuide}
        size="md"
        testId="inspector-template-guide"
        title="이 양식에 꼭 맞출 필요는 없어요"
      />

      {/* 파일 잘림 경고 — 앞부분만 검사할지, 파일을 나눠 다시 올릴지 반드시 골라야 한다 */}
      <Dialog
        description={pendingTruncatedInspection?.warning}
        dismissible={false}
        footer={(
          <ModalActions
            primary={{
              label: '앞부분만 검사하기',
              onClick: () => {
                const students = pendingTruncatedInspection?.students || [];
                setPendingTruncatedInspection(null);
                handleBatchCheckInternal(students);
              },
            }}
            secondary={{
              label: '파일 다시 올리기',
              onClick: () => {
                setPendingTruncatedInspection(null);
                setUploadError(null);
                setBatchFile(null);
                setBatchStep(1);
              },
            }}
            testId="inspector-truncation-warning-actions"
          />
        )}
        open={Boolean(pendingTruncatedInspection)}
        role="alertdialog"
        size="md"
        testId="inspector-truncation-warning"
        title="파일 일부만 검사돼요"
      >
        <Notice variant="warning">
          이대로 진행하면 포함된 앞부분 학생만 검사될 수 있어요. 전체 학급을 검사하려면 파일을 나눠서 다시 올려주세요.
        </Notice>
      </Dialog>

      {/* 스캔 PDF 인식 결과 확인 — 학생 수·학번·이름이 맞는지 보고 검사 시작 */}
      <Dialog
        description={(() => {
          const meta = pendingScanPreview?.meta || {};
          const count = pendingScanPreview?.students?.length || 0;
          const pages = meta.pages_total ? `${meta.pages_total}페이지에서 ` : '';
          return `${pages}${count}명을 찾았어요. 학생 수와 이름이 맞는지 확인한 뒤 검사를 시작해주세요.`;
        })()}
        dismissible={false}
        footer={(
          <ModalActions
            primary={{
              label: '이대로 검사 시작',
              onClick: () => {
                const students = pendingScanPreview?.students || [];
                setPendingScanPreview(null);
                handleBatchCheckInternal(students);
              },
            }}
            secondary={{
              label: '파일 다시 올리기',
              onClick: () => {
                setPendingScanPreview(null);
                setUploadError(null);
                setBatchFile(null);
                setBatchStep(1);
              },
            }}
            testId="inspector-scan-preview-actions"
          />
        )}
        open={Boolean(pendingScanPreview)}
        size="md"
        testId="inspector-scan-preview"
        title="스캔 파일에서 읽은 학생 목록"
      >
        {(pendingScanPreview?.meta?.rotations_fixed || 0) > 0 && (
          <Notice variant="info">
            돌아가 있던 페이지 {pendingScanPreview.meta.rotations_fixed}장을 바로 세워서 읽었어요.
          </Notice>
        )}
        <ul className="inspector-scan-preview-list" data-testid="inspector-scan-preview-list">
          {(pendingScanPreview?.students || []).map((student, index) => (
            <li key={student.id || index} className="inspector-scan-preview-item">
              <span className="inspector-scan-preview-no">{student.student_no || index + 1}</span>
              <span className="inspector-scan-preview-name">{student.name}</span>
              {student.subject && <span className="inspector-scan-preview-subject">{student.subject}</span>}
              <span className="inspector-scan-preview-len">{(student.content || '').length}자</span>
            </li>
          ))}
        </ul>
      </Dialog>

      {/* 🔍 파일 사전 검사 모달 */}
      <FilePreCheckModal
        isOpen={showPreCheckModal}
        onClose={handlePreCheckCancel}
        onConfirm={handlePreCheckConfirm}
        checkResult={preCheckResult}
        isLoading={preCheckLoading}
      />
    </div>
  );
};

export default SaenggibuInspector;
